import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  PolicyScenario,
  PolicyImpactResults,
  calculatePolicyImpact,
  getDefaultScenario,
  getPredefinedScenarios,
} from '../../services/policyImpactService';
import { getMicromobilityStats, MicromobilityStats } from '../../services/policyImpactService';
import {
  InterventionAreas,
  getDefaultInterventionAreas,
  distributeEnergyEfficiencyTarget,
  getDefaultEnergyEfficiencyComponents,
} from '../../types/policyImpact';
import {
  computeTotalEfficiencyPercentFromComponents,
  applyEfficiencyLayer,
  applyEfficiencyForYearWithContributions,
  COMMERCIAL_ANNUAL_GROWTH_RATE,
  type EfficiencyLayerOptions,
  type EfficiencyContributions,
} from '../../services/buildingsEfficiencyModel';
import { getPathwayTherms, getPathwayKwh } from '../../services/buildingsBauPathways';
import { getCachedBaseline } from '../../services/policyBaselineCache';
import type { EnergyEfficiencySubPolicyValues } from './InterventionAreasControl';
import {
  EnergyEfficiencyOverTimeChart,
  EfficiencyByPolicyChart,
  type EnergyEfficiencyOverTimeData,
} from './EnergyEfficiencyCharts';
import PolicyImpactChart from './PolicyImpactChart';
import ParkingPricingPolicy from './ParkingPricingPolicy';
import VehicleElectrificationPolicy from './VehicleElectrificationPolicy';
import BuildingElectrificationPolicy from './BuildingElectrificationPolicy';
import InterventionAreasControl from './InterventionAreasControl';
import TransitServiceExpansionCharts from './TransitServiceExpansionCharts';
import CompactDevelopmentCharts from './CompactDevelopmentCharts';
import { fetchCompactDevelopmentSeries, fetchHousingUnitsByYear, CompactDevelopmentSeries, GROWTH_RATE_PERCENT_MAX, COMPACT_DEVELOPMENT_MIN_DENSITY } from '../../services/compactDevelopmentService';
import { RNG_SHARE_2045_BY_SCENARIO } from '../../services/buildingsBauPathways';
import type { TimeSeriesEmissionData } from '../../services/policyTimeSeriesService';
import {
  generatePolicyTimeSeriesData,
  generateFlatBaselineTimeSeries,
  getBuildingEnergyTimeSeries,
} from '../../services/policyTimeSeriesService';
import type { BuildingEnergyComparisonSeries } from './buildingElectrificationChartTypes';
import { getFlatBuildingsPathway } from '../../services/buildingsBauPathways';
import { computeBuildingsCostResults, type BuildingBillImpactDetail } from '../../services/buildingsCostModel';
import { computeTransportCostResults, ICE_MPG, EV_KWH_PER_MILE } from '../../services/transportCostModel';
import { ELECTRICITY_PRICE_PER_KWH } from '../../services/buildingsCostModel';
import {
  DEFAULT_COST_ASSUMPTIONS,
  costAssumptionsToBuildingsOverrides,
  type CostAssumptions,
} from '../../services/costAssumptions';
import { fetchLatestElectricityPricesForLocation } from '../../services/electricityPricesByGeographyService';
import { fetchLatestTotalEstablishmentsForGeofips } from '../../services/businessEstablishmentsService';
import { getDefaultBuildingEndUseAssumptions } from '../../services/buildingEndUseAssumptions';
import {
  fetchCommercialGasEndUseResolved,
  commercialSharesNearDefaults,
} from '../../services/commercialGasEndUseService';
import { ScenarioCostAnalysisSection } from './ScenarioCostAnalysisSection';
import { PortfolioCostSection } from './PortfolioCostSection';
import { PolicyToolCostAssumptionsSection } from './PolicyToolCostAssumptionsSection';
import { currencyShort } from './portfolioCostUtils';
import { supabaseClient } from '../../services/supabase';
import { loadScenarioById, saveScenario } from '../../services/scenarioService';
import { useAuth } from '../../contexts/AuthContext';
import {
  parseScenarioFromUrl,
  validateScenarioUrlParams,
  applyScenarioPresetAndOverrides,
  updateScenarioUrl,
  buildScenarioStateFromTool,
  serializeScenarioState,
  hydrateToolFromScenarioState,
  type LocalPresetParam,
} from '../../utils/scenarioUrl';
import type { ScenarioReportInput } from '../../utils/scenarioReportGenerator';
import './PolicyImpactTool.css';

/** Building-only emissions by year (MT CO₂e), for Building Electrification panel charts */
export interface BuildingEmissionsTimeSeries {
  baseline: Array<{ year: number; naturalGas: number; electricity: number }>;
  policy: Array<{ year: number; naturalGas: number; electricity: number }>;
}

const CHART_YEAR_MIN = 2023;
const CHART_YEAR_MAX = 2045;

function deriveBuildingEmissionsTimeSeries(
  baselineData: TimeSeriesEmissionData[],
  projectedData: TimeSeriesEmissionData[]
): BuildingEmissionsTimeSeries {
  const filter = (rows: TimeSeriesEmissionData[]) =>
    rows
      .filter((d) => d.year >= CHART_YEAR_MIN && d.year <= CHART_YEAR_MAX)
      .map((d) => ({
        year: d.year,
        naturalGas: d.naturalGasEmissions ?? 0,
        electricity: d.electricityEmissions ?? 0,
      }));
  return {
    baseline: filter(baselineData),
    policy: filter(projectedData),
  };
}

interface PolicyImpactToolProps {
  location: string;
  geofips?: number | null;
}

const TRANSIT_ELASTICITY = 0.7; // Handy et al. 2013
const TRANSIT_MODE_SHARE_PERCENT = 20; // Placeholder until wired to Census
const TRANSIT_STATEWIDE_MODE_SHIFT_PERCENT = 57.8; // FHWA 2017
const TRANSIT_IMPLEMENTATION_YEAR = 2025;
const TRANSIT_PROJECTION_END_YEAR = 2045;

const calculateTransitVmtReductionPercent = (serviceIncreasePercent: number): number => {
  const vmtReductionPct =
    TRANSIT_ELASTICITY *
    (serviceIncreasePercent / 100) *
    (TRANSIT_MODE_SHARE_PERCENT / 100) *
    (TRANSIT_STATEWIDE_MODE_SHIFT_PERCENT / 100) *
    100;
  return Math.max(0, Number(vmtReductionPct.toFixed(3)));
};

const PolicyImpactTool: React.FC<PolicyImpactToolProps> = ({ location, geofips = null }) => {
  if (typeof process !== 'undefined' && process.env.NODE_ENV === 'development') {
    console.count('PolicyImpactTool:render');
  }
  const { user } = useAuth();
  const [scenario, setScenario] = useState<PolicyScenario>(getDefaultScenario());
  const [results, setResults] = useState<PolicyImpactResults | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [interventionAreas, setInterventionAreas] = useState<InterventionAreas>(getDefaultInterventionAreas());
  const [evMinPercent, setEvMinPercent] = useState<number>(0);
  const [activeChart, setActiveChart] = useState<'line' | 'contributions' | 'forecast' | 'stacked'>('stacked');
  const [targetReductionPercent, setTargetReductionPercent] = useState<number>(80);
  const [targetBaselineYear, setTargetBaselineYear] = useState<number>(2005);
  const [stackedTotals, setStackedTotals] = useState<null | {
    baselineYear: number;
    baselineTotal: number;
    projectedYear: number;
    projectedWithPoliciesTotal: number;
    totalReduction: number;
    percentReduction: number;
  }>(null);
  const [expandedIntervention, setExpandedIntervention] = useState<keyof InterventionAreas | null>(null);
  const [expandedSubPolicy, setExpandedSubPolicy] = useState<string | null>(null);
  const [subPolicyValues, setSubPolicyValues] = useState({
    transitOrientedDevelopment: 0,
    downtownHomesPercent: 70,
    activeTransportation: 0,
    transitImprovements: 0,
    parkingPricing: 0,
    micromobility: 0,
    otherInitiatives: 0,
  });
  const [micromobilityStats, setMicromobilityStats] = useState<MicromobilityStats | null>(null);
  const [micromobilityVMTReductionPercent, setMicromobilityVMTReductionPercent] = useState<number>(0);
  const [transitRevenueMiles, setTransitRevenueMiles] = useState<{ year: number; miles: number } | null>(null);
  const [transitServiceHistory, setTransitServiceHistory] = useState<Array<{ year: number; miles: number }>>([]);
  const [transitVMTReductionPercent, setTransitVMTReductionPercent] = useState<number>(0);
  const [emfacVersion, setEmfacVersion] = useState<'2021' | '2025'>('2021');
  const [buildingsBauScenario, setBuildingsBauScenario] = useState<'conservative' | 'current_policy' | 'accelerated'>('current_policy');
  const [localScenarioName, setLocalScenarioName] = useState<LocalPresetParam | undefined>(undefined);
  const [scenarioId, setScenarioId] = useState<string | undefined>(undefined);
  const [savingScenario, setSavingScenario] = useState<boolean>(false);
  const [copyLinkStatus, setCopyLinkStatus] = useState<'idle' | 'success' | 'error'>('idle');
  /** Default: incremental vs BAU so portfolio cash flow matches emissions baseline vs scenario (transport $≈0 when only buildings change). */
  const [portfolioCostView, setPortfolioCostView] = useState<'incremental_vs_bau' | 'bau_costs' | 'total_costs'>('incremental_vs_bau');
  const [buildingsRngPercent, setBuildingsRngPercent] = useState<number>(() => RNG_SHARE_2045_BY_SCENARIO.current_policy * 100);
  const [buildingsCop, setBuildingsCop] = useState<number>(3.5); // Heat pump COP 2.5–4; 3.5 = efficient unit in mild climate
  const [buildingEmissionsTimeSeries, setBuildingEmissionsTimeSeries] = useState<BuildingEmissionsTimeSeries | null>(null);
  /** Full time series for Buildings Cost layer (baseline + projected emissions). */
  const [timeSeriesDataForCost, setTimeSeriesDataForCost] = useState<{
    baselineData: TimeSeriesEmissionData[];
    projectedData: TimeSeriesEmissionData[];
  } | null>(null);
  const [compactDevelopmentSeries, setCompactDevelopmentSeries] = useState<CompactDevelopmentSeries | null>(null);
  const [efficiencyChartData, setEfficiencyChartData] = useState<EnergyEfficiencyOverTimeData | null>(null);
  const [efficiencyContributions2045, setEfficiencyContributions2045] = useState<EfficiencyContributions | null>(null);
  const [efficiencySegmentShares, setEfficiencySegmentShares] = useState<{
    resExisting2030: number;
    resExisting2045: number;
    comExisting2030: number;
    comExisting2045: number;
  } | null>(null);
  /** Housing units by year for building electrification CAPEX ($/unit). Fetched when geofips available. */
  const [housingUnitsByYear, setHousingUnitsByYear] = useState<Record<number, number> | null>(null);
  /** business_establishments total row — scales commercial intensity in buildings cost model. */
  const [commercialEstablishmentCount, setCommercialEstablishmentCount] = useState<number | null>(null);
  const [electricityPriceContext, setElectricityPriceContext] = useState<{
    pricePerKwh: number;
    source: string;
  }>({
    pricePerKwh: ELECTRICITY_PRICE_PER_KWH,
    source: 'Default building cost assumption',
  });
  /** Drives portfolio buildings/transport cost, decision summary, and scenario cost comparison. */
  const [policyCostAssumptions, setPolicyCostAssumptions] = useState<CostAssumptions>(DEFAULT_COST_ASSUMPTIONS);
  const [policyCostIceMpg, setPolicyCostIceMpg] = useState(ICE_MPG);
  const [policyCostEvKwhPerMile, setPolicyCostEvKwhPerMile] = useState(EV_KWH_PER_MILE);
  const urlInitializedRef = React.useRef(false);
  /** False until first-pass URL / saved-scenario hydration finishes (avoids predefined scenario overwriting geography commercial shares). */
  const [urlScenarioHydrated, setUrlScenarioHydrated] = useState(false);

  useEffect(() => {
    setPolicyCostAssumptions((a) =>
      a.electricityPricePerKwh === electricityPriceContext.pricePerKwh
        ? a
        : { ...a, electricityPricePerKwh: electricityPriceContext.pricePerKwh },
    );
  }, [electricityPriceContext.pricePerKwh]);

  const resetPolicyCostAssumptions = useCallback(() => {
    setPolicyCostAssumptions({
      ...DEFAULT_COST_ASSUMPTIONS,
      electricityPricePerKwh: electricityPriceContext.pricePerKwh,
    });
    setPolicyCostIceMpg(ICE_MPG);
    setPolicyCostEvKwhPerMile(EV_KWH_PER_MILE);
  }, [electricityPriceContext.pricePerKwh]);

  const policyBuildingsCostAssumptions = useMemo(
    () => costAssumptionsToBuildingsOverrides(policyCostAssumptions),
    [policyCostAssumptions],
  );

  const policyTransportCostOptionsBase = useMemo(
    () => ({
      gasolinePricePerGallon: policyCostAssumptions.gasolinePricePerGallon,
      electricityPricePerKwh: policyCostAssumptions.electricityPricePerKwh,
      discountRate: policyCostAssumptions.discountRate,
      iceMpg: policyCostIceMpg,
      evKwhPerMile: policyCostEvKwhPerMile,
      evIncrementalCost: policyCostAssumptions.evIncrementalCost,
      chargerCostPerEv: policyCostAssumptions.chargerCostPerEv,
      emfacVersion,
    }),
    [policyCostAssumptions, policyCostIceMpg, policyCostEvKwhPerMile, emfacVersion],
  );

  /** Housing-based efficiency options: existing vs new residential share from compact development when available. */
  const efficiencyLayerOptions: EfficiencyLayerOptions | undefined = useMemo(() => {
    const rows = compactDevelopmentSeries?.rows ?? [];
    if (rows.length === 0) return undefined;
    const r2023 = rows.find((r) => r.year === 2023);
    if (!r2023?.housingUnitsBau || r2023.housingUnitsBau <= 0) return undefined;
    return {
      // Clamped to [0,1] to handle edge cases where housing declines.
      existingResShareByYear: (year: number) => {
        const rY = rows.find((r) => r.year === year);
        if (!rY?.housingUnitsBau || rY.housingUnitsBau <= 0) return Math.max(0, Math.min(1, 1 / Math.pow(1.02, year - 2023)));
        return Math.max(0, Math.min(1, r2023.housingUnitsBau / rY.housingUnitsBau));
      },
    };
  }, [compactDevelopmentSeries]);

  /** Stable reference for ScenarioCostAnalysisSection — inline object was new every render and blew runComparison/debounce deps. */
  const scenarioCostAnalysisOptions = useMemo(
    () => ({
      buildingsBauScenario,
      buildingsRngPercent,
      buildingsCop,
      emfacVersion,
      efficiencyLayerOptionsByLocation: efficiencyLayerOptions ? { [location]: efficiencyLayerOptions } : undefined,
    }),
    [buildingsBauScenario, buildingsRngPercent, buildingsCop, emfacVersion, location, efficiencyLayerOptions],
  );

  const handleTimeSeriesDataChange = React.useCallback((baselineData: TimeSeriesEmissionData[], projectedData: TimeSeriesEmissionData[]) => {
    setBuildingEmissionsTimeSeries(deriveBuildingEmissionsTimeSeries(baselineData, projectedData));
    setTimeSeriesDataForCost({ baselineData, projectedData });
  }, []);

  // When Buildings BAU scenario changes, reset RNG to that scenario's default (user can override in Building Electrification panel)
  useEffect(() => {
    setBuildingsRngPercent(RNG_SHARE_2045_BY_SCENARIO[buildingsBauScenario] * 100);
  }, [buildingsBauScenario]);
  const [compactDevelopmentLoading, setCompactDevelopmentLoading] = useState<boolean>(false);

  // Compute parking-pricing-only VMT reduction percent (consistent with service logic)
  const computeParkingVMTReductionPercent = (assumptions: PolicyScenario['parkingPricing']): number => {
    if (assumptions.priceIncreasePercent === 0) return 0;
    const SF_COMMUTE_BY_CAR_PERCENT = 30.29;
    const BASE_ELASTICITY = -0.4;
    if (assumptions.commuteByCarPercent <= 0 || assumptions.commuteByCarPercent >= 100) return 0;
    const elasticity = BASE_ELASTICITY * (SF_COMMUTE_BY_CAR_PERCENT / assumptions.commuteByCarPercent);
    const vmtReductionPercent = Math.abs(elasticity) * (assumptions.priceIncreasePercent / 100);
    const effective = vmtReductionPercent * (assumptions.vmtAffectedPercent / 100) * 100; // percent
    return Math.max(0, Math.min(20, Number(effective.toFixed(1))));
  };

  // Update scenario's parking pricing priceIncreasePercent when menu slider changes
  useEffect(() => {
    setScenario(prev => ({
      ...prev,
      parkingPricing: {
        ...prev.parkingPricing,
        priceIncreasePercent: subPolicyValues.parkingPricing,
      },
    }));
  }, [subPolicyValues.parkingPricing]);

  // Calculate parking pricing and micromobility VMT reduction percentages
  const parkingVMTReductionPercent = computeParkingVMTReductionPercent(scenario.parkingPricing);

  // Update total VMT reduction when calculated percentages change (transit, parking, micromobility)
  // or when direct policy values change
  // When VMT Reduction is expanded, preserve the total and let Other Initiatives adjust
  // When collapsed, set total = sum of policy reductions when policies change
  useEffect(() => {
    // Recalculate parking VMT reduction using the latest parking pricing value
    // Use subPolicyValues.parkingPricing directly to avoid stale values
    const currentParkingVMT = computeParkingVMTReductionPercent({
      ...scenario.parkingPricing,
      priceIncreasePercent: subPolicyValues.parkingPricing,
    });
    
    // Compact Development percent growth is excluded: VMT impact comes from model net statewide, not this sum
    const sumOfPolicyReductions =
      (subPolicyValues.activeTransportation || 0) +
      transitVMTReductionPercent +
      currentParkingVMT +
      micromobilityVMTReductionPercent;
    
    // If VMT Reduction is expanded, preserve the total unless Other Initiatives would go to zero or negative
    if (expandedIntervention === 'vmtReduction') {
      setInterventionAreas(prev => {
        const currentTotal = prev.vmtReduction;
        const calculatedOtherInitiatives = currentTotal - sumOfPolicyReductions;
        if (calculatedOtherInitiatives <= 0) {
          const newTotal = Math.max(0, Math.min(20, sumOfPolicyReductions));
          if (Math.abs(currentTotal - newTotal) > 0.01) {
            return { ...prev, vmtReduction: newTotal };
          }
        }
        return prev;
      });
      return;
    }
    
    // When collapsed and policies change, set total to sum of policy reductions (excluding compact growth)
    const newTotal = Math.max(0, Math.min(20, sumOfPolicyReductions));
    setInterventionAreas(prev => {
      if (Math.abs(prev.vmtReduction - newTotal) > 0.01) {
        return { ...prev, vmtReduction: newTotal };
      }
      return prev;
    });
  }, [scenario.parkingPricing, subPolicyValues.parkingPricing, transitVMTReductionPercent, micromobilityVMTReductionPercent, subPolicyValues.activeTransportation, expandedIntervention]);

  // Update scenario's vmtReductionPercent when interventionAreas.vmtReduction changes
  useEffect(() => {
    setScenario(prev => ({
      ...prev,
      vmtReductionPercent: interventionAreas.vmtReduction,
    }));
  }, [interventionAreas.vmtReduction]);

  // Sync scenario.downtownHomesPercent from subPolicyValues.downtownHomesPercent
  useEffect(() => {
    setScenario(prev => ({
      ...prev,
      downtownHomesPercent: subPolicyValues.downtownHomesPercent ?? 70,
    }));
  }, [subPolicyValues.downtownHomesPercent]);

  // Update Other Initiatives when the main VMT Reduction slider is manually changed
  // Sum excludes Compact Development (its VMT impact is from model net statewide)
  useEffect(() => {
    const currentParkingVMT = computeParkingVMTReductionPercent(scenario.parkingPricing);
    const sumOfPolicyReductions =
      (subPolicyValues.activeTransportation || 0) +
      transitVMTReductionPercent +
      currentParkingVMT +
      micromobilityVMTReductionPercent;
    const calculatedOtherInitiatives = Math.max(0, interventionAreas.vmtReduction - sumOfPolicyReductions);
    setSubPolicyValues(prev => {
      if (Math.abs((prev.otherInitiatives || 0) - calculatedOtherInitiatives) > 0.01) {
        return {
          ...prev,
          otherInitiatives: calculatedOtherInitiatives,
        };
      }
      return prev;
    });
  }, [interventionAreas.vmtReduction, subPolicyValues.activeTransportation, scenario.parkingPricing, transitVMTReductionPercent, micromobilityVMTReductionPercent]);

  // Sync sub-policy values with scenario's VMT reduction components
  // Note: transitImprovements is NOT synced here because subPolicyValues.transitImprovements
  // stores the service increase % (0-50%), while scenario.vmtReductionComponents.transitImprovements
  // stores the calculated VMT reduction % (0-10%). They are different units.
  useEffect(() => {
    setSubPolicyValues(prev => ({
      ...prev,
      transitOrientedDevelopment: scenario.vmtReductionComponents.transitOrientedDevelopment,
      activeTransportation: scenario.vmtReductionComponents.activeTransportation,
      // transitImprovements is NOT synced - it's the service increase %, not VMT reduction %
    }));
  }, [scenario.vmtReductionComponents.transitOrientedDevelopment, scenario.vmtReductionComponents.activeTransportation]);

  // Sync sub-policy values back to scenario when they change
  // Note: transitImprovements is handled separately above (stores calculated VMT %)
  useEffect(() => {
    setScenario(prev => ({
      ...prev,
      vmtReductionComponents: {
        ...prev.vmtReductionComponents,
        transitOrientedDevelopment: subPolicyValues.transitOrientedDevelopment,
        activeTransportation: subPolicyValues.activeTransportation,
        // transitImprovements is handled in the transit service calculation effect above
      },
    }));
  }, [subPolicyValues.transitOrientedDevelopment, subPolicyValues.activeTransportation]);

  // Keep micromobility scale in scenario when slider changes
  useEffect(() => {
    setScenario(prev => ({
      ...prev,
      micromobility: {
        ...(prev.micromobility || {
          yearImplemented: 2025,
          tripsPerResident: 3,
          percentBike: 60,
          percentScooter: 40,
          milesPerBikeTrip: 1.61,
          milesPerScooterTrip: 1.2,
          percentBikeTripsDisplaceCars: 30,
          percentScooterTripsDisplaceCars: 20,
          kwhPerMileBike: 0.02,
          kwhPerMileScooter: 0.04,
          scalePercent: 0,
        }),
        scalePercent: subPolicyValues.micromobility || 0,
      },
    }));
  }, [subPolicyValues.micromobility]);

  // Compute Transit Service Improvements -> VMT reduction percent
  useEffect(() => {
    const increasePct = subPolicyValues.transitImprovements || 0; // % increase in service
    const pctRounded = calculateTransitVmtReductionPercent(increasePct);
    setTransitVMTReductionPercent(pctRounded);
    
    // Store the calculated VMT reduction % in the scenario (not the service increase %)
    setScenario(prev => ({
      ...prev,
      vmtReductionComponents: {
        ...prev.vmtReductionComponents,
        transitImprovements: pctRounded, // Store calculated VMT reduction %, not service increase %
      },
    }));
  }, [subPolicyValues.transitImprovements]);

  // Load micromobility detailed stats whenever scenario/location changes
  useEffect(() => {
    const loadStats = async () => {
      if (!location) return;
      try {
        const stats = await getMicromobilityStats(location, {
          ...scenario,
          micromobility: {
            ...(scenario.micromobility || {
              yearImplemented: 2025,
              tripsPerResident: 3,
              percentBike: 60,
              percentScooter: 40,
              milesPerBikeTrip: 1.61,
              milesPerScooterTrip: 1.2,
              percentBikeTripsDisplaceCars: 30,
              percentScooterTripsDisplaceCars: 20,
              kwhPerMileBike: 0.02,
              kwhPerMileScooter: 0.04,
              scalePercent: 0,
            }),
            scalePercent: subPolicyValues.micromobility || 0,
          }
        } as PolicyScenario, emfacVersion);
        setMicromobilityStats(stats);
        
        // Calculate VMT reduction percentage from displaced car miles
        if (stats.displacedCarMiles > 0 && results?.baseline?.vmt) {
          const vmtReductionPercent = (stats.displacedCarMiles / results.baseline.vmt) * 100;
          setMicromobilityVMTReductionPercent(Math.max(0, Math.min(20, Number(vmtReductionPercent.toFixed(1)))));
        } else {
          setMicromobilityVMTReductionPercent(0);
        }
      } catch (e) {
        console.warn('Failed to load micromobility stats', e);
        setMicromobilityStats(null);
        setMicromobilityVMTReductionPercent(0);
      }
    };
    if (results?.baseline?.vmt) {
      loadStats();
    }
  }, [location, scenario, subPolicyValues.micromobility, results?.baseline?.vmt, emfacVersion]);

  // Load baseline transit revenue miles (vehicle revenue miles) for the selected location
  useEffect(() => {
    const loadTransitMiles = async () => {
      try {
        // Prefer geofips if provided; fallback to searching by name
        let row: Record<string, unknown> | null = null;
        if (geofips) {
          const { data } = await supabaseClient
            .from('vehicle_revenue_miles')
            .select('*')
            .eq('geofips', String(geofips))
            .maybeSingle();
          row = (data as Record<string, unknown> | null) || null;
        }
        if (!row) {
          const { data } = await supabaseClient
            .from('vehicle_revenue_miles')
            .select('*')
            .ilike('geo_name', `%${location}%`)
            .in('location_type', ['City', 'Place'])
            .limit(1)
            .maybeSingle();
          row = (data as Record<string, unknown> | null) || null;
        }

        if (row) {
          const history = Object.entries(row)
            .map(([key, rawValue]) => {
              if (!/^\d{4}$/.test(key)) return null;
              const year = Number(key);
              const miles = Number(String(rawValue ?? '').replace(/,/g, ''));
              if (!Number.isFinite(year) || !Number.isFinite(miles) || miles <= 0) return null;
              return { year, miles };
            })
            .filter((point): point is { year: number; miles: number } => point !== null)
            .sort((a, b) => a.year - b.year);

          setTransitServiceHistory(history);

          const latestThrough2023 = [...history].reverse().find((point) => point.year <= 2023);
          const latestAvailable = latestThrough2023 || history[history.length - 1];
          if (latestAvailable) {
            setTransitRevenueMiles(latestAvailable);
            return;
          }
        }

        setTransitServiceHistory([]);
        setTransitRevenueMiles(null);
      } catch (e) {
        console.warn('Failed to load vehicle_revenue_miles for', location, e);
        setTransitServiceHistory([]);
        setTransitRevenueMiles(null);
      }
    };
    loadTransitMiles();
  }, [location, geofips]);

  // Load compact development time series when we have geofips and (user expanded Compact Development OR compact > 0)
  // so the headline forecast can use net statewide savings. Uses same value as main slider: subPolicyValues.transitOrientedDevelopment (0–2%).
  useEffect(() => {
    const shouldFetch =
      geofips &&
      (expandedSubPolicy === 'transitOrientedDevelopment' || (subPolicyValues.transitOrientedDevelopment ?? 0) > 0);
    if (!shouldFetch) {
      if (expandedSubPolicy !== 'transitOrientedDevelopment') setCompactDevelopmentSeries(null);
      return;
    }
    let cancelled = false;
    setCompactDevelopmentLoading(true);
    // Include historical years (e.g. through 2023) so we get city data; service projects 2024+
    const startYear = 2015;
    const endYear = 2045;
    const growthOverride = typeof subPolicyValues.transitOrientedDevelopment === 'number' ? subPolicyValues.transitOrientedDevelopment : undefined;
    fetchCompactDevelopmentSeries(geofips, startYear, endYear, 'incremental_baseline', growthOverride, location)
      .then((series) => {
        if (!cancelled) {
          console.log('[Compact Development] selected location density', {
            location,
            geofips,
            populationDensityPerSqMi: series?.populationDensityPerSqMi ?? null,
            densityDataMissing: series?.densityDataMissing,
            recommendedForLocation: series?.recommendedForLocation,
          });
          setCompactDevelopmentSeries(series ?? null);
        }
      })
      .catch(() => {
        if (!cancelled) setCompactDevelopmentSeries(null);
      })
      .finally(() => {
        if (!cancelled) setCompactDevelopmentLoading(false);
      });
    return () => { cancelled = true; };
  }, [expandedSubPolicy, geofips, location, subPolicyValues.transitOrientedDevelopment]);

  // Do not auto-set Compact Development slider so that "no sliders moved" = no transport savings in Local Policy view.
  // User can set it explicitly when expanding VMT Reduction → Compact Development or by loading a predefined scenario.

  // Initialize vehicleElectrification with current EV percentage when baseline data loads (only once)
  useEffect(() => {
    if (results?.baseline.currentEVPercent !== undefined) {
      const currentEV = results.baseline.currentEVPercent;
      setEvMinPercent(currentEV);
      setInterventionAreas(prev => {
        // Only update if different to avoid unnecessary updates
        if (prev.vehicleElectrification !== currentEV) {
          return {
            ...prev,
            vehicleElectrification: currentEV,
          };
        }
        return prev;
      });
      // Also set evAdoptionPercent to match
      setScenario(prev => {
        if (prev.evAdoptionPercent !== currentEV) {
          return {
            ...prev,
            evAdoptionPercent: currentEV,
          };
        }
        return prev;
      });
    }
  }, [results?.baseline.currentEVPercent]);

  // Default grid decarbonization lever is now the clean electricity year (SB 100 default: 2045).
  useEffect(() => {
    setInterventionAreas((prev) => ({
      ...prev,
      gridDecarbonization: prev.gridDecarbonization || 2045,
    }));
    setScenario((prev) => ({
      ...prev,
      gridCleanElectricityYear: (prev as any).gridCleanElectricityYear || 2045,
    } as any));
  }, []);

  // Sync vehicleElectrification slider with evAdoptionPercent
  useEffect(() => {
    setScenario(prev => ({
      ...prev,
      // Do not allow going below the current EV share.
      evAdoptionPercent: Math.max(evMinPercent || 0, interventionAreas.vehicleElectrification),
    }));
  }, [interventionAreas.vehicleElectrification, evMinPercent]);

  // Sync Energy Efficiency: target mode (collapsed) — slider drives target and distributed components
  useEffect(() => {
    if (expandedIntervention === 'energyEfficiency') return; // Policy mode: slider locked, total from components
    const eff = Math.max(0, Math.min(40, interventionAreas.energyEfficiency ?? 0));
    setScenario(prev => {
      const current = prev.energyEfficiencyComponents ?? getDefaultEnergyEfficiencyComponents();
      const distributed = distributeEnergyEfficiencyTarget(eff, current);
      return {
        ...prev,
        energyEfficiencyTargetPercent: eff,
        energyEfficiencyComponents: distributed,
        energyEfficiencyPercent: eff,
      };
    });
  }, [interventionAreas.energyEfficiency, expandedIntervention]);

  // Sync Building Electrification slider with scenario heatPumpAdoptionPercent (NG reduction; electricity impact later).
  // When Energy Efficiency slider is 0, keep scenario efficiency at 0 so "only building electrification" gives correct cost (policy elec > BAU, negative operating savings).
  useEffect(() => {
    const bldgElec = Math.max(0, Math.min(100, interventionAreas.buildingsElectrification || 0));
    const eff = interventionAreas.energyEfficiency ?? 0;
    setScenario(prev => {
      const next = { ...prev, heatPumpAdoptionPercent: bldgElec };
      if (eff === 0) {
        next.energyEfficiencyTargetPercent = 0;
        next.energyEfficiencyPercent = 0;
        next.energyEfficiencyComponents = getDefaultEnergyEfficiencyComponents();
      }
      return next;
    });
  }, [interventionAreas.buildingsElectrification, interventionAreas.energyEfficiency]);

  // Sync Organics Diversion slider with scenario organicsDiversionPercent (50-100%)
  useEffect(() => {
    const organics = Math.max(50, Math.min(100, interventionAreas.organicsDiversion || 50));
    setScenario(prev => ({
      ...prev,
      organicsDiversionPercent: organics,
    }));
  }, [interventionAreas.organicsDiversion]);

  // Sync Grid Decarbonization slider (year) with scenario.gridCleanElectricityYear
  useEffect(() => {
    const raw = Number(interventionAreas.gridDecarbonization || 2045);
    const clamped = Math.max(2025, Math.min(2045, raw));
    setScenario(prev => ({
      ...prev,
      gridCleanElectricityYear: clamped,
    } as any));
  }, [interventionAreas.gridDecarbonization]);

  // When Energy Efficiency main slider > 0 or panel expanded, compute chart data and segment shares from cached baseline
  useEffect(() => {
    const showEfficiencyCharts = expandedIntervention === 'energyEfficiency';
    if (!showEfficiencyCharts || !location) {
      setEfficiencyChartData(null);
      setEfficiencyContributions2045(null);
      setEfficiencySegmentShares(null);
      return;
    }
    const cacheKey = `baseline:${location}:emfac${emfacVersion}:buildingsBau${buildingsBauScenario}`;
    const baseline = getCachedBaseline(cacheKey);
    const pathway = baseline?.buildingsBauPathway;
    if (!pathway?.years?.length || !pathway.thermsByYear?.length || !pathway.kwhByYear?.length) {
      setEfficiencyChartData(null);
      setEfficiencyContributions2045(null);
      setEfficiencySegmentShares(null);
      return;
    }
    const years = pathway.years.filter((y) => y >= 2023 && y <= 2045);
    const bauThermsByYear = years.map((y) => getPathwayTherms(pathway, y));
    const bauKwhByYear = years.map((y) => getPathwayKwh(pathway, y));
    const components = scenario.energyEfficiencyComponents && (
      (scenario.energyEfficiencyComponents.residentialReachCodePercent ?? 0) > 0 ||
      (scenario.energyEfficiencyComponents.commercialReachCodePercent ?? 0) > 0 ||
      (scenario.energyEfficiencyComponents.weatherizationPercent ?? 0) > 0 ||
      (scenario.energyEfficiencyComponents.demandResponsePercent ?? 0) > 0 ||
      (scenario.energyEfficiencyComponents.otherEfficiencyPercent ?? 0) > 0
    )
      ? scenario.energyEfficiencyComponents
      : distributeEnergyEfficiencyTarget(
          Math.max(0, Math.min(40, (scenario as any).energyEfficiencyTargetPercent ?? (scenario as any).energyEfficiencyPercent ?? 0)),
          scenario.energyEfficiencyComponents ?? getDefaultEnergyEfficiencyComponents()
        );
    const result = applyEfficiencyLayer(bauThermsByYear, bauKwhByYear, years, components, efficiencyLayerOptions);
    setEfficiencyChartData({
      years,
      bauTherms: bauThermsByYear,
      bauKwh: bauKwhByYear,
      effTherms: result.thermsByYear,
      effKwh: result.kwhByYear,
    });
    const idx2045 = years.indexOf(2045);
    if (idx2045 >= 0) {
      const withContrib = applyEfficiencyForYearWithContributions(
        bauThermsByYear[idx2045] ?? 0,
        bauKwhByYear[idx2045] ?? 0,
        2045,
        components,
        efficiencyLayerOptions
      );
      setEfficiencyContributions2045(withContrib.contributions);
    } else {
      setEfficiencyContributions2045(null);
    }
    const resExisting2030 = efficiencyLayerOptions?.existingResShareByYear?.(2030) ?? 1 / Math.pow(1.02, 2030 - 2023);
    const resExisting2045 = efficiencyLayerOptions?.existingResShareByYear?.(2045) ?? 1 / Math.pow(1.02, 2045 - 2023);
    const comExisting2030 = efficiencyLayerOptions?.existingComShareByYear?.(2030) ?? 1 / Math.pow(1 + COMMERCIAL_ANNUAL_GROWTH_RATE, 2030 - 2023);
    const comExisting2045 = efficiencyLayerOptions?.existingComShareByYear?.(2045) ?? 1 / Math.pow(1 + COMMERCIAL_ANNUAL_GROWTH_RATE, 2045 - 2023);
    setEfficiencySegmentShares({
      resExisting2030,
      resExisting2045,
      comExisting2030,
      comExisting2045,
    });
  }, [expandedIntervention, interventionAreas.energyEfficiency, location, emfacVersion, buildingsBauScenario, scenario, efficiencyLayerOptions]);

  /** BAU vs policy building therms/kWh for Building Electrification panel charts (matches cost / bill pipeline). */
  const buildingEnergyComparisonSeries: BuildingEnergyComparisonSeries | null = useMemo(() => {
    const baselineCacheKey = `baseline:${location}:emfac${emfacVersion}:buildingsBau${buildingsBauScenario}`;
    const baseline = getCachedBaseline(baselineCacheKey);
    if (!baseline) return null;
    try {
      const energy = getBuildingEnergyTimeSeries(baseline, scenario, {
        rngOverridePercent: buildingsRngPercent,
        cop: buildingsCop,
        efficiencyLayerOptions,
      });
      const years = energy.years.filter((y) => y >= CHART_YEAR_MIN && y <= CHART_YEAR_MAX);
      const yi = (y: number) => energy.years.indexOf(y);
      return {
        labels: years.map(String),
        bauTherms: years.map((y) => energy.bauThermsByYear[yi(y)] ?? 0),
        bauKwh: years.map((y) => energy.bauKwhByYear[yi(y)] ?? 0),
        policyTherms: years.map((y) => energy.policyThermsByYear[yi(y)] ?? 0),
        policyKwh: years.map((y) => energy.policyKwhByYear[yi(y)] ?? 0),
      };
    } catch {
      return null;
    }
  }, [location, emfacVersion, buildingsBauScenario, scenario, buildingsRngPercent, buildingsCop, efficiencyLayerOptions]);

  /** Buildings cost layer: uses cached baseline + time series; costView selects BAU vs Local vs Total. */
  const buildingsCostResults = useMemo(() => {
    const cacheKey = `baseline:${location}:emfac${emfacVersion}:buildingsBau${buildingsBauScenario}`;
    const baseline = getCachedBaseline(cacheKey);
    if (!baseline || !timeSeriesDataForCost) return null;
    const flatBaselineData = portfolioCostView !== 'incremental_vs_bau' ? generateFlatBaselineTimeSeries(baseline) : null;
    const flatPathway = portfolioCostView !== 'incremental_vs_bau' ? getFlatBuildingsPathway(baseline) : undefined;
    let emissionsResults = timeSeriesDataForCost;
    if (portfolioCostView === 'bau_costs' && flatBaselineData) {
      emissionsResults = { baselineData: flatBaselineData, projectedData: timeSeriesDataForCost.baselineData };
    } else if (portfolioCostView === 'total_costs' && flatBaselineData) {
      emissionsResults = { baselineData: flatBaselineData, projectedData: timeSeriesDataForCost.projectedData };
    }
    const out = computeBuildingsCostResults({
      location,
      baseline,
      policyScenario: scenario,
      buildingsBauScenario,
      buildingsRngPercentOverride: buildingsRngPercent,
      buildingsCop,
      efficiencyLayerOptions,
      emissionsResults,
      referencePathway: flatPathway,
      comparisonTarget: portfolioCostView === 'bau_costs' ? 'bau' : portfolioCostView === 'total_costs' ? 'policy' : undefined,
      housingUnitsByYear: housingUnitsByYear ?? undefined,
      commercialEstablishmentCount: commercialEstablishmentCount ?? undefined,
      assumptions: policyBuildingsCostAssumptions,
    });
    if (typeof process !== 'undefined' && process.env.NODE_ENV === 'development') {
      const m = out.costEffectivenessMetrics;
      const ok = m && [m.upfrontCapitalCost, m.cumulativeNetCost, m.netPresentValue].every((v) => typeof v === 'number' && !Number.isNaN(v));
      console.log('Cost results:', ok ? 'OK' : 'check', { upfront: m.upfrontCapitalCost, cumulative: m.cumulativeNetCost, npv: m.netPresentValue });
    }
    return out;
  }, [location, emfacVersion, buildingsBauScenario, scenario, buildingsRngPercent, buildingsCop, efficiencyLayerOptions, timeSeriesDataForCost, portfolioCostView, housingUnitsByYear, commercialEstablishmentCount, policyBuildingsCostAssumptions]);

  /** Transport cost for current location; uses same cost view as buildings (flat vs BAU vs policy). */
  const transportCostResults = useMemo(() => {
    const cacheKey = `baseline:${location}:emfac${emfacVersion}:buildingsBau${buildingsBauScenario}`;
    const baseline = getCachedBaseline(cacheKey);
    if (!baseline || !timeSeriesDataForCost) return null;
    const flatBaselineData = portfolioCostView !== 'incremental_vs_bau' ? generateFlatBaselineTimeSeries(baseline) : null;
    let transportTimeSeries = timeSeriesDataForCost;
    if (portfolioCostView === 'bau_costs' && flatBaselineData) {
      transportTimeSeries = { baselineData: flatBaselineData, projectedData: timeSeriesDataForCost.baselineData };
    } else if (portfolioCostView === 'total_costs' && flatBaselineData) {
      transportTimeSeries = { baselineData: flatBaselineData, projectedData: timeSeriesDataForCost.projectedData };
    }
    const transportScenario =
      portfolioCostView === 'bau_costs'
        ? { ...scenario, evAdoptionPercent: baseline.currentEVPercent2045 ?? 20, vmtReductionPercent: 0 }
        : scenario;
    return computeTransportCostResults({
      baseline,
      timeSeries: transportTimeSeries,
      scenario: transportScenario,
      options: { ...policyTransportCostOptionsBase, costView: portfolioCostView },
    });
  }, [location, emfacVersion, buildingsBauScenario, scenario, timeSeriesDataForCost, portfolioCostView, policyTransportCostOptionsBase]);

  // Cost decomposition for Decision Summary narrative:
  // - BAU/state-policy-only costs
  // - Local incremental costs above BAU
  const buildingsCostBau = useMemo(() => {
    const cacheKey = `baseline:${location}:emfac${emfacVersion}:buildingsBau${buildingsBauScenario}`;
    const baseline = getCachedBaseline(cacheKey);
    if (!baseline || !timeSeriesDataForCost) return null;
    const flatBaselineData = generateFlatBaselineTimeSeries(baseline);
    const flatPathway = getFlatBuildingsPathway(baseline);
    return computeBuildingsCostResults({
      location,
      baseline,
      policyScenario: scenario,
      buildingsBauScenario,
      buildingsRngPercentOverride: buildingsRngPercent,
      buildingsCop,
      efficiencyLayerOptions,
      emissionsResults: { baselineData: flatBaselineData, projectedData: timeSeriesDataForCost.baselineData },
      referencePathway: flatPathway,
      comparisonTarget: 'bau',
      housingUnitsByYear: housingUnitsByYear ?? undefined,
      commercialEstablishmentCount: commercialEstablishmentCount ?? undefined,
      assumptions: policyBuildingsCostAssumptions,
    });
  }, [location, emfacVersion, buildingsBauScenario, scenario, buildingsRngPercent, buildingsCop, efficiencyLayerOptions, timeSeriesDataForCost, housingUnitsByYear, commercialEstablishmentCount, policyBuildingsCostAssumptions]);

  const transportCostBau = useMemo(() => {
    const cacheKey = `baseline:${location}:emfac${emfacVersion}:buildingsBau${buildingsBauScenario}`;
    const baseline = getCachedBaseline(cacheKey);
    if (!baseline || !timeSeriesDataForCost) return null;
    const flatBaselineData = generateFlatBaselineTimeSeries(baseline);
    return computeTransportCostResults({
      baseline,
      timeSeries: { baselineData: flatBaselineData, projectedData: timeSeriesDataForCost.baselineData },
      scenario: { ...scenario, evAdoptionPercent: baseline.currentEVPercent2045 ?? 20, vmtReductionPercent: 0 },
      options: { ...policyTransportCostOptionsBase, costView: 'bau_costs' },
    });
  }, [location, emfacVersion, buildingsBauScenario, scenario, timeSeriesDataForCost, policyTransportCostOptionsBase]);

  const buildingsCostLocalIncremental = useMemo(() => {
    const cacheKey = `baseline:${location}:emfac${emfacVersion}:buildingsBau${buildingsBauScenario}`;
    const baseline = getCachedBaseline(cacheKey);
    if (!baseline || !timeSeriesDataForCost) return null;
    return computeBuildingsCostResults({
      location,
      baseline,
      policyScenario: scenario,
      buildingsBauScenario,
      buildingsRngPercentOverride: buildingsRngPercent,
      buildingsCop,
      efficiencyLayerOptions,
      emissionsResults: timeSeriesDataForCost,
      housingUnitsByYear: housingUnitsByYear ?? undefined,
      commercialEstablishmentCount: commercialEstablishmentCount ?? undefined,
      assumptions: policyBuildingsCostAssumptions,
    });
  }, [location, emfacVersion, buildingsBauScenario, scenario, buildingsRngPercent, buildingsCop, efficiencyLayerOptions, timeSeriesDataForCost, housingUnitsByYear, commercialEstablishmentCount, policyBuildingsCostAssumptions]);

  const transportCostLocalIncremental = useMemo(() => {
    const cacheKey = `baseline:${location}:emfac${emfacVersion}:buildingsBau${buildingsBauScenario}`;
    const baseline = getCachedBaseline(cacheKey);
    if (!baseline || !timeSeriesDataForCost) return null;
    return computeTransportCostResults({
      baseline,
      timeSeries: timeSeriesDataForCost,
      scenario,
      options: { ...policyTransportCostOptionsBase, costView: 'incremental_vs_bau' },
    });
  }, [location, emfacVersion, buildingsBauScenario, scenario, timeSeriesDataForCost, policyTransportCostOptionsBase]);

  // Fetch housing units by year for building electrification CAPEX ($/unit) when geofips available.
  useEffect(() => {
    if (!geofips || !Number.isFinite(geofips)) {
      setHousingUnitsByYear(null);
      return;
    }
    let cancelled = false;
    fetchHousingUnitsByYear(geofips, 2023, 2045).then((byYear) => {
      if (!cancelled) setHousingUnitsByYear(byYear);
    }).catch(() => {
      if (!cancelled) setHousingUnitsByYear(null);
    });
    return () => { cancelled = true; };
  }, [geofips]);

  useEffect(() => {
    if (geofips == null || !Number.isFinite(geofips)) {
      setCommercialEstablishmentCount(null);
      return;
    }
    let cancelled = false;
    fetchLatestTotalEstablishmentsForGeofips(geofips)
      .then((n) => {
        if (!cancelled) setCommercialEstablishmentCount(n);
      })
      .catch(() => {
        if (!cancelled) setCommercialEstablishmentCount(null);
      });
    return () => {
      cancelled = true;
    };
  }, [geofips]);

  // Commercial gas end-use % from CBECS 2018 + CBP-weighted table (when user has not customized defaults).
  useEffect(() => {
    if (!urlScenarioHydrated) return;
    if (geofips == null || !Number.isFinite(geofips)) return;
    let cancelled = false;
    fetchCommercialGasEndUseResolved(geofips).then((row) => {
      if (cancelled || !row) return;
      setScenario((prev) => {
        if (!commercialSharesNearDefaults(prev.buildingEndUseAssumptions?.commercial)) {
          return prev;
        }
        return {
          ...prev,
          buildingEndUseAssumptions: {
            ...getDefaultBuildingEndUseAssumptions(),
            ...prev.buildingEndUseAssumptions,
            commercial: {
              spaceHeating: row.spaceHeating,
              waterHeating: row.waterHeating,
              cooking: row.cooking,
              other: row.other,
            },
          },
        };
      });
    });
    return () => {
      cancelled = true;
    };
  }, [geofips, urlScenarioHydrated]);

  // Resolve location-specific electricity price when available; fallback to assumptions default.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!geofips || !Number.isFinite(geofips)) {
        setElectricityPriceContext({
          pricePerKwh: ELECTRICITY_PRICE_PER_KWH,
          source: 'Default building cost assumption',
        });
        return;
      }
      try {
        const price = await fetchLatestElectricityPricesForLocation(geofips);
        if (cancelled) return;
        const resolved = price.buildingPricePerKwh ?? price.average ?? ELECTRICITY_PRICE_PER_KWH;
        const source = price.utilityName
          ? `Supabase utility price (${price.utilityName}${price.year ? `, ${price.year}` : ''})`
          : `Supabase location price${price.year ? ` (${price.year})` : ''}`;
        setElectricityPriceContext({ pricePerKwh: resolved, source });
      } catch {
        if (cancelled) return;
        setElectricityPriceContext({
          pricePerKwh: ELECTRICITY_PRICE_PER_KWH,
          source: 'Default building cost assumption (Supabase price unavailable)',
        });
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [geofips]);

  // Ensure time series is available for cost when results exist. Refetch when scenario (or other cost-relevant params) changes
  // so buildings/transport cost charts stay in sync with sliders (fixes phantom spikes when only Vehicle Electrification changes).
  useEffect(() => {
    if (!location || !results) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await generatePolicyTimeSeriesData(location, scenario, emfacVersion, buildingsBauScenario, efficiencyLayerOptions ? { efficiencyLayerOptions } : undefined);
        if (!cancelled && data?.baselineData?.length && data?.projectedData?.length) {
          setTimeSeriesDataForCost({ baselineData: data.baselineData, projectedData: data.projectedData });
        }
      } catch (_) {
        // ignore
      }
    })();
    return () => { cancelled = true; };
  }, [location, results, scenario, emfacVersion, buildingsBauScenario, efficiencyLayerOptions]);

  useEffect(() => {
    if (typeof process !== 'undefined' && process.env.NODE_ENV === 'development' && results) {
      console.log('Cost results:', buildingsCostResults != null ? { hasData: true, upfront: buildingsCostResults.costEffectivenessMetrics?.upfrontCapitalCost, cumulative: buildingsCostResults.costEffectivenessMetrics?.cumulativeNetCost } : { hasData: false });
    }
  }, [results, buildingsCostResults]);

  // Calculate impact whenever scenario or location changes
  // Add a ref to track if we're currently calculating to prevent loops
  const calculatingRef = React.useRef(false);
  
  useEffect(() => {
    const calculateImpact = async () => {
      if (!location) return;
      
      // Prevent concurrent calculations
      if (calculatingRef.current) {
        console.log('PolicyImpactTool: Calculation already in progress, skipping...');
        return;
      }

      calculatingRef.current = true;
      setLoading(true);
      setError(null);

      try {
        const policyYear = 2045;
        const compactNetStatewideMT =
          compactDevelopmentSeries?.rows?.find((r) => r.year === policyYear)?.netStatewideAnnual ??
          compactDevelopmentSeries?.rows?.slice(-1)[0]?.netStatewideAnnual;
        const options = {
          ...(typeof compactNetStatewideMT === 'number' && compactNetStatewideMT > 0
            ? { compactDevelopmentLdvReductionMT: compactNetStatewideMT }
            : {}),
          buildingsBauScenario,
          buildingsRngPercentOverride: buildingsRngPercent,
          buildingsCopOverride: buildingsCop,
          ...(efficiencyLayerOptions ? { efficiencyLayerOptions } : {}),
        };
        const impact = await calculatePolicyImpact(location, scenario, emfacVersion, options);
        setResults(impact);
      } catch (err) {
        console.error('Error calculating policy impact:', err);
        setError('Failed to calculate policy impact. Please try again.');
        // Don't set results to null on error to avoid clearing previous data
      } finally {
        setLoading(false);
        calculatingRef.current = false;
      }
    };

    // Debounce calculations to avoid too many updates
    const timeoutId = setTimeout(calculateImpact, 500);
    return () => {
      clearTimeout(timeoutId);
      calculatingRef.current = false;
    };
  }, [location, scenario, emfacVersion, buildingsBauScenario, buildingsRngPercent, buildingsCop, compactDevelopmentSeries, efficiencyLayerOptions]);

  const handleLoadPredefinedScenario = (scenarioName: string) => {
    if (
      scenarioName !== 'baseline' &&
      scenarioName !== 'moderate' &&
      scenarioName !== 'aggressive' &&
      scenarioName !== 'fullImplementation'
    ) {
      return;
    }
    const predefined = getPredefinedScenarios();
    if (predefined[scenarioName]) {
      setLocalScenarioName(scenarioName);
      const loadedScenario = predefined[scenarioName];
      setScenario(loadedScenario);
      const effTarget = loadedScenario.energyEfficiencyTargetPercent ?? loadedScenario.energyEfficiencyPercent ?? 0;
      setInterventionAreas({
        urbanInfill: 0,
        vmtReduction: loadedScenario.vmtReductionPercent,
        vehicleElectrification: loadedScenario.evAdoptionPercent,
        energyEfficiency: effTarget,
        buildingsElectrification: loadedScenario.heatPumpAdoptionPercent,
        gridDecarbonization: loadedScenario.gridCleanElectricityYear,
        organicsDiversion: loadedScenario.organicsDiversionPercent,
      });
      setSubPolicyValues(prev => ({
        ...prev,
        transitOrientedDevelopment: loadedScenario.vmtReductionComponents.transitOrientedDevelopment,
        downtownHomesPercent: loadedScenario.downtownHomesPercent ?? 70,
        activeTransportation: loadedScenario.vmtReductionComponents.activeTransportation,
        transitImprovements: loadedScenario.vmtReductionComponents.transitImprovements,
        parkingPricing: loadedScenario.parkingPricing.priceIncreasePercent,
        micromobility: loadedScenario.micromobility?.scalePercent || 0,
      }));
    }
  };

  const handleResetScenario = () => {
    setLocalScenarioName('baseline');
    const defaultScenario = getDefaultScenario();
    setScenario(defaultScenario);
    const effTarget = defaultScenario.energyEfficiencyTargetPercent ?? 0;
    setInterventionAreas({
      urbanInfill: 0,
      vmtReduction: defaultScenario.vmtReductionPercent,
      vehicleElectrification: defaultScenario.evAdoptionPercent,
      energyEfficiency: effTarget,
      buildingsElectrification: defaultScenario.heatPumpAdoptionPercent,
      gridDecarbonization: defaultScenario.gridCleanElectricityYear,
      organicsDiversion: defaultScenario.organicsDiversionPercent,
    });
    setSubPolicyValues({
      transitOrientedDevelopment: defaultScenario.vmtReductionComponents.transitOrientedDevelopment,
      downtownHomesPercent: defaultScenario.downtownHomesPercent ?? 70,
      activeTransportation: defaultScenario.vmtReductionComponents.activeTransportation,
      transitImprovements: defaultScenario.vmtReductionComponents.transitImprovements,
      parkingPricing: defaultScenario.parkingPricing.priceIncreasePercent,
      micromobility: defaultScenario.micromobility?.scalePercent || 0,
      otherInitiatives: 0,
    });
  };

  // Initialize from URL params once on first mount.
  useEffect(() => {
    if (urlInitializedRef.current) return;
    urlInitializedRef.current = true;

    (async () => {
      try {
        const parsedUrlParams = parseScenarioFromUrl();
        const { params: urlParams, invalidKeys } = validateScenarioUrlParams(parsedUrlParams);

        // Priority 1: scenarioId from Supabase
        if (urlParams.scenarioId) {
          try {
            setScenarioId(urlParams.scenarioId);
            const saved = await loadScenarioById(urlParams.scenarioId);
            if (saved?.scenario_json) {
              const hydrated = hydrateToolFromScenarioState(saved.scenario_json);
              if (hydrated.emfacVersion) setEmfacVersion(hydrated.emfacVersion);
              if (hydrated.buildingsBauScenario) setBuildingsBauScenario(hydrated.buildingsBauScenario);
              if (hydrated.localScenarioName) handleLoadPredefinedScenario(hydrated.localScenarioName);
              setLocalScenarioName(hydrated.localScenarioName);
              if (hydrated.interventionAreas) {
                setInterventionAreas((prev) => ({
                  ...prev,
                  vehicleElectrification: hydrated.interventionAreas?.vehicleElectrification ?? prev.vehicleElectrification,
                  vmtReduction: hydrated.interventionAreas?.vmtReduction ?? prev.vmtReduction,
                  energyEfficiency: hydrated.interventionAreas?.energyEfficiency ?? prev.energyEfficiency,
                  buildingsElectrification: hydrated.interventionAreas?.buildingsElectrification ?? prev.buildingsElectrification,
                  gridDecarbonization: hydrated.interventionAreas?.gridDecarbonization ?? prev.gridDecarbonization,
                  organicsDiversion: hydrated.interventionAreas?.organicsDiversion ?? prev.organicsDiversion,
                }));
              }
              if (hydrated.subPolicyValues) {
                setSubPolicyValues((prev) => ({
                  ...prev,
                  transitOrientedDevelopment: hydrated.subPolicyValues?.compactDevelopment ?? prev.transitOrientedDevelopment,
                  downtownHomesPercent: hydrated.subPolicyValues?.downtownHomesPercent ?? prev.downtownHomesPercent,
                  activeTransportation: hydrated.subPolicyValues?.activeTransportation ?? prev.activeTransportation,
                  transitImprovements: hydrated.subPolicyValues?.transitImprovements ?? prev.transitImprovements,
                  parkingPricing: hydrated.subPolicyValues?.parkingPricing ?? prev.parkingPricing,
                  micromobility: hydrated.subPolicyValues?.micromobility ?? prev.micromobility,
                }));
              }
              if (hydrated.energyEfficiencyComponents) {
                setScenario((prev) => ({
                  ...prev,
                  energyEfficiencyComponents: {
                    ...(prev.energyEfficiencyComponents ?? getDefaultEnergyEfficiencyComponents()),
                    weatherizationPercent:
                      hydrated.energyEfficiencyComponents?.weatherizationPercent ??
                      prev.energyEfficiencyComponents?.weatherizationPercent ??
                      0,
                    residentialReachCodePercent:
                      hydrated.energyEfficiencyComponents?.residentialReachCodePercent ??
                      prev.energyEfficiencyComponents?.residentialReachCodePercent ??
                      0,
                    commercialReachCodePercent:
                      hydrated.energyEfficiencyComponents?.commercialReachCodePercent ??
                      prev.energyEfficiencyComponents?.commercialReachCodePercent ??
                      0,
                    demandResponsePercent:
                      hydrated.energyEfficiencyComponents?.demandResponsePercent ??
                      prev.energyEfficiencyComponents?.demandResponsePercent ??
                      0,
                    otherEfficiencyPercent:
                      hydrated.energyEfficiencyComponents?.otherEfficiencyPercent ??
                      prev.energyEfficiencyComponents?.otherEfficiencyPercent ??
                      0,
                  },
                }));
              }
              if (process.env.NODE_ENV === 'development') {
                console.debug('[PolicyImpactTool] loaded scenarioId', urlParams.scenarioId, hydrated);
              }
              return;
            }
          } catch (e) {
            if (process.env.NODE_ENV === 'development') {
              console.warn('[PolicyImpactTool] failed to load scenarioId; falling back to URL params', urlParams.scenarioId, e);
            }
          }
        }

        // Priority 2: readable URL params
        if (urlParams.transport === 'emfac2025' || urlParams.transport === 'emfac2021') {
          setEmfacVersion(urlParams.transport === 'emfac2025' ? '2025' : '2021');
        }

        if (urlParams.buildings === 'conservative' || urlParams.buildings === 'accelerated' || urlParams.buildings === 'current_policy' || urlParams.buildings === 'bau') {
          const mapped = urlParams.buildings === 'bau' ? 'current_policy' : urlParams.buildings;
          setBuildingsBauScenario(mapped);
        }

        if (urlParams.local) {
          handleLoadPredefinedScenario(urlParams.local);
        }

        const resolution = applyScenarioPresetAndOverrides(urlParams);
        const applied = resolution.values;
        if (process.env.NODE_ENV === 'development') {
          console.debug('[PolicyImpactTool] URL hydration', {
            parsed: parsedUrlParams,
            validated: urlParams,
            invalidKeys,
            appliedPreset: resolution.preset,
            overridesApplied: {
              ev: urlParams.ev,
              vmt: urlParams.vmt,
              elecYear: urlParams.elecYear,
            },
          });
        }
        setInterventionAreas((prev) => ({
          ...prev,
          vehicleElectrification:
            applied.ev != null ? Math.max(0, Math.min(100, applied.ev)) : prev.vehicleElectrification,
          vmtReduction:
            applied.vmt != null ? Math.max(0, Math.min(20, applied.vmt)) : prev.vmtReduction,
          gridDecarbonization:
            applied.elecYear != null ? Math.max(2025, Math.min(2045, Math.round(applied.elecYear))) : prev.gridDecarbonization,
          energyEfficiency:
            urlParams.energyEfficiency != null ? Math.max(0, Math.min(40, urlParams.energyEfficiency)) : prev.energyEfficiency,
          buildingsElectrification:
            urlParams.buildingsElectrification != null
              ? Math.max(0, Math.min(100, urlParams.buildingsElectrification))
              : prev.buildingsElectrification,
          organicsDiversion:
            urlParams.organics != null ? Math.max(50, Math.min(100, urlParams.organics)) : prev.organicsDiversion,
        }));
        setSubPolicyValues((prev) => ({
          ...prev,
          transitOrientedDevelopment:
            urlParams.compactDevelopment != null
              ? Math.max(0, Math.min(3, urlParams.compactDevelopment))
              : prev.transitOrientedDevelopment,
          downtownHomesPercent:
            urlParams.downtownHomes != null ? Math.max(0, Math.min(100, urlParams.downtownHomes)) : prev.downtownHomesPercent,
          activeTransportation:
            urlParams.activeTransportation != null
              ? Math.max(0, Math.min(10, urlParams.activeTransportation))
              : prev.activeTransportation,
          transitImprovements:
            urlParams.transitImprovements != null
              ? Math.max(0, Math.min(50, urlParams.transitImprovements))
              : prev.transitImprovements,
          parkingPricing:
            urlParams.parkingPricing != null ? Math.max(0, Math.min(100, urlParams.parkingPricing)) : prev.parkingPricing,
          micromobility:
            urlParams.micromobility != null ? Math.max(0, Math.min(200, urlParams.micromobility)) : prev.micromobility,
        }));
        setScenario((prev) => ({
          ...prev,
          energyEfficiencyComponents: {
            ...(prev.energyEfficiencyComponents ?? getDefaultEnergyEfficiencyComponents()),
            weatherizationPercent:
              urlParams.weatherization != null ? Math.max(0, Math.min(100, urlParams.weatherization)) : prev.energyEfficiencyComponents?.weatherizationPercent ?? 0,
            residentialReachCodePercent:
              urlParams.residentialReachCode != null
                ? Math.max(0, Math.min(100, urlParams.residentialReachCode))
                : prev.energyEfficiencyComponents?.residentialReachCodePercent ?? 0,
            commercialReachCodePercent:
              urlParams.commercialReachCode != null
                ? Math.max(0, Math.min(100, urlParams.commercialReachCode))
                : prev.energyEfficiencyComponents?.commercialReachCodePercent ?? 0,
            demandResponsePercent:
              urlParams.demandResponse != null
                ? Math.max(0, Math.min(100, urlParams.demandResponse))
                : prev.energyEfficiencyComponents?.demandResponsePercent ?? 0,
            otherEfficiencyPercent:
              urlParams.otherEfficiency != null
                ? Math.max(0, Math.min(100, urlParams.otherEfficiency))
                : prev.energyEfficiencyComponents?.otherEfficiencyPercent ?? 0,
          },
        }));
      } finally {
        setUrlScenarioHydrated(true);
      }
    })();
  // Intentionally run once; values are read from URL at mount time.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep URL params in sync with key scenario controls.
  useEffect(() => {
    updateScenarioUrl({
      transport: emfacVersion === '2025' ? 'emfac2025' : 'emfac2021',
      buildings: buildingsBauScenario === 'current_policy' ? 'bau' : buildingsBauScenario,
      local: localScenarioName as LocalPresetParam | undefined,
      ev: Math.round(interventionAreas.vehicleElectrification * 10) / 10,
      vmt: Math.round(interventionAreas.vmtReduction * 10) / 10,
      elecYear: Math.round(interventionAreas.gridDecarbonization),
      energyEfficiency: Math.round((interventionAreas.energyEfficiency ?? 0) * 10) / 10,
      buildingsElectrification: Math.round((interventionAreas.buildingsElectrification ?? 0) * 10) / 10,
      organics: Math.round(interventionAreas.organicsDiversion ?? 50),
      compactDevelopment: Math.round((subPolicyValues.transitOrientedDevelopment ?? 0) * 10) / 10,
      downtownHomes: Math.round(subPolicyValues.downtownHomesPercent ?? 70),
      activeTransportation: Math.round((subPolicyValues.activeTransportation ?? 0) * 10) / 10,
      transitImprovements: Math.round((subPolicyValues.transitImprovements ?? 0) * 10) / 10,
      parkingPricing: Math.round((subPolicyValues.parkingPricing ?? 0) * 10) / 10,
      micromobility: Math.round((subPolicyValues.micromobility ?? 0) * 10) / 10,
      weatherization: Math.round((scenario.energyEfficiencyComponents?.weatherizationPercent ?? 0) * 10) / 10,
      residentialReachCode: Math.round((scenario.energyEfficiencyComponents?.residentialReachCodePercent ?? 0) * 10) / 10,
      commercialReachCode: Math.round((scenario.energyEfficiencyComponents?.commercialReachCodePercent ?? 0) * 10) / 10,
      demandResponse: Math.round((scenario.energyEfficiencyComponents?.demandResponsePercent ?? 0) * 10) / 10,
      otherEfficiency: Math.round((scenario.energyEfficiencyComponents?.otherEfficiencyPercent ?? 0) * 10) / 10,
      scenarioId,
    });
  }, [
    emfacVersion,
    buildingsBauScenario,
    localScenarioName,
    scenarioId,
    subPolicyValues.transitOrientedDevelopment,
    subPolicyValues.downtownHomesPercent,
    subPolicyValues.activeTransportation,
    subPolicyValues.transitImprovements,
    subPolicyValues.parkingPricing,
    subPolicyValues.micromobility,
    scenario.energyEfficiencyComponents?.weatherizationPercent,
    scenario.energyEfficiencyComponents?.residentialReachCodePercent,
    scenario.energyEfficiencyComponents?.commercialReachCodePercent,
    scenario.energyEfficiencyComponents?.demandResponsePercent,
    scenario.energyEfficiencyComponents?.otherEfficiencyPercent,
    interventionAreas.vehicleElectrification,
    interventionAreas.vmtReduction,
    interventionAreas.energyEfficiency,
    interventionAreas.buildingsElectrification,
    interventionAreas.organicsDiversion,
    interventionAreas.gridDecarbonization,
  ]);

  const handleSaveScenario = React.useCallback(async () => {
    if (!user?.id) {
      console.warn('Save Scenario requires sign-in.');
      return;
    }
    try {
      setSavingScenario(true);
      const state = buildScenarioStateFromTool({
        location,
        emfacVersion,
        buildingsBauScenario,
        localScenarioName,
        interventionAreas: {
          vehicleElectrification: interventionAreas.vehicleElectrification,
          vmtReduction: interventionAreas.vmtReduction,
          energyEfficiency: interventionAreas.energyEfficiency,
          buildingsElectrification: interventionAreas.buildingsElectrification,
          gridDecarbonization: interventionAreas.gridDecarbonization,
          organicsDiversion: interventionAreas.organicsDiversion,
        },
        subPolicyValues: {
          compactDevelopment: subPolicyValues.transitOrientedDevelopment,
          downtownHomesPercent: subPolicyValues.downtownHomesPercent,
          activeTransportation: subPolicyValues.activeTransportation,
          transitImprovements: subPolicyValues.transitImprovements,
          parkingPricing: subPolicyValues.parkingPricing,
          micromobility: subPolicyValues.micromobility,
        },
        energyEfficiencyComponents: {
          weatherizationPercent: scenario.energyEfficiencyComponents?.weatherizationPercent,
          residentialReachCodePercent: scenario.energyEfficiencyComponents?.residentialReachCodePercent,
          commercialReachCodePercent: scenario.energyEfficiencyComponents?.commercialReachCodePercent,
          demandResponsePercent: scenario.energyEfficiencyComponents?.demandResponsePercent,
          otherEfficiencyPercent: scenario.energyEfficiencyComponents?.otherEfficiencyPercent,
        },
      });
      const scenarioJson = serializeScenarioState(state);
      const saved = await saveScenario({
        name: null,
        description: null,
        location,
        transport_scenario: state.emfacVersion === '2025' ? 'emfac2025' : 'emfac2021',
        buildings_scenario: state.buildingsBauScenario === 'current_policy' ? 'bau' : state.buildingsBauScenario,
        local_preset: state.localScenarioName ?? null,
        scenario_json: scenarioJson,
        cost_assumptions: null,
        results_json: null,
        model_version: 'policy-tool-v2',
        is_public: false,
        created_by: user.id,
      });
      setScenarioId(saved.id);
      if (process.env.NODE_ENV === 'development') {
        console.debug('[PolicyImpactTool] saved scenarioId', saved.id);
      }
    } catch (e) {
      console.error('Failed to save scenario', e);
    } finally {
      setSavingScenario(false);
    }
  }, [
    location,
    emfacVersion,
    buildingsBauScenario,
    localScenarioName,
    interventionAreas.vehicleElectrification,
    interventionAreas.vmtReduction,
    interventionAreas.energyEfficiency,
    interventionAreas.buildingsElectrification,
    interventionAreas.organicsDiversion,
    interventionAreas.gridDecarbonization,
    subPolicyValues.transitOrientedDevelopment,
    subPolicyValues.downtownHomesPercent,
    subPolicyValues.activeTransportation,
    subPolicyValues.transitImprovements,
    subPolicyValues.parkingPricing,
    subPolicyValues.micromobility,
    scenario.energyEfficiencyComponents?.weatherizationPercent,
    scenario.energyEfficiencyComponents?.residentialReachCodePercent,
    scenario.energyEfficiencyComponents?.commercialReachCodePercent,
    scenario.energyEfficiencyComponents?.demandResponsePercent,
    scenario.energyEfficiencyComponents?.otherEfficiencyPercent,
    user?.id,
  ]);

  const handleCopyShareableLink = React.useCallback(async () => {
    try {
      if (typeof window === 'undefined') return;
      const shareUrl = new URL(window.location.href);
      shareUrl.searchParams.delete('scenarioId');
      const shareHref = shareUrl.toString();
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareHref);
        setCopyLinkStatus('success');
        window.setTimeout(() => setCopyLinkStatus('idle'), 1800);
        return;
      }
      // Fallback for non-secure contexts / older browsers
      const el = document.createElement('textarea');
      el.value = shareHref;
      el.style.position = 'fixed';
      el.style.left = '-9999px';
      document.body.appendChild(el);
      el.focus();
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      setCopyLinkStatus('success');
      window.setTimeout(() => setCopyLinkStatus('idle'), 1800);
    } catch (e) {
      console.warn('Failed to copy link', e);
      setCopyLinkStatus('error');
      window.setTimeout(() => setCopyLinkStatus('idle'), 2200);
    }
  }, []);

  // Energy efficiency sub-policy values from scenario (policy mode when expanded)
  const energyEfficiencySubPolicyValues: EnergyEfficiencySubPolicyValues = (() => {
    const c = scenario.energyEfficiencyComponents ?? getDefaultEnergyEfficiencyComponents();
    return {
      residentialReachCodePercent: c.residentialReachCodePercent ?? 0,
      commercialReachCodePercent: c.commercialReachCodePercent ?? 0,
      weatherizationPercent: c.weatherizationPercent ?? 0,
      demandResponsePercent: c.demandResponsePercent ?? 0,
      otherEfficiencyPercent: c.otherEfficiencyPercent ?? 0,
    };
  })();

  const handleEnergyEfficiencySubPolicyChange = React.useCallback(
    (values: EnergyEfficiencySubPolicyValues) => {
      const components = {
        ...values,
        otherEfficiencyPercent: 0, // policy mode: total from levers only
      };
      const bauT = results?.baseline?.bauTherms2045 ?? 1;
      const bauK = results?.baseline?.bauKwh2045 ?? 1;
      const total = computeTotalEfficiencyPercentFromComponents(components, bauT, bauK);
      setScenario(prev => ({
        ...prev,
        energyEfficiencyComponents: components,
        energyEfficiencyTargetPercent: total,
        energyEfficiencyPercent: total,
      }));
      setInterventionAreas(prev => ({ ...prev, energyEfficiency: total }));
    },
    [results?.baseline?.bauTherms2045, results?.baseline?.bauKwh2045]
  );

  const decisionSummary = useMemo(() => {
    if (!results) return null;

    const fmtPct = (value: number, total: number): number => (total > 0 ? (value / total) * 100 : 0);
    const formatRate = (value: number): string => `$${value.toFixed(3)}`;

    const buildingsUpfront = buildingsCostResults?.costEffectivenessMetrics?.upfrontCapitalCost ?? 0;
    const transportUpfront = transportCostResults?.upfrontCapitalCost ?? 0;
    const totalCapitalInvestment = buildingsUpfront + transportUpfront;

    const buildingsNpv = buildingsCostResults?.costEffectivenessMetrics?.netPresentValue ?? 0;
    const transportNpv = transportCostResults?.netPresentValue ?? 0;
    const netSavingsNpv = buildingsNpv + transportNpv;

    const buildingsCumulative = buildingsCostResults?.costEffectivenessMetrics?.cumulativeNetCost ?? 0;
    const transportCumulative = transportCostResults?.cumulativeNetCost ?? 0;
    const totalCumulativeNetCost = buildingsCumulative + transportCumulative;

    const emissionsReductionMT =
      (buildingsCostResults?.totalBuildingEmissionsReductionMT ?? 0) + (transportCostResults?.emissionsReductionMT ?? 0);
    const costPerTon = emissionsReductionMT > 0 ? totalCumulativeNetCost / emissionsReductionMT : null;
    const buildingAnnualOperatingSavings2045 =
      buildingsCostResults?.annualSavingsSeries?.[buildingsCostResults.annualSavingsSeries.length - 1]?.value ?? 0;
    const transportAnnualOperatingSavings2045 =
      transportCostResults?.annualFuelSavingsSeries?.[transportCostResults.annualFuelSavingsSeries.length - 1]?.value ?? 0;
    const annualOperatingSavings2045 = buildingAnnualOperatingSavings2045 + transportAnnualOperatingSavings2045;
    const cumulativeOperatingSavings =
      (buildingsCostResults?.annualSavingsSeries?.reduce((sum, row) => sum + row.value, 0) ?? 0) +
      (transportCostResults?.annualFuelSavingsSeries?.reduce((sum, row) => sum + row.value, 0) ?? 0);

    const sectorRows = [
      {
        key: 'transport',
        label: 'Transport',
        tons: (results.reductions.lightDutyReduction ?? 0) + (results.reductions.heavyDutyReduction ?? 0),
      },
      {
        key: 'buildings',
        label: 'Buildings',
        tons: (results.reductions.naturalGasReduction ?? 0) + (results.reductions.electricityReduction ?? 0),
      },
      {
        key: 'waste',
        label: 'Waste',
        tons: results.reductions.wasteReduction ?? 0,
      },
    ];
    const totalReduction = Math.max(0, results.reductions.totalReduction ?? 0);
    const sectors = sectorRows.map((r) => ({ ...r, percent: fmtPct(r.tons, totalReduction) }));

    const localPolicyRowsBase = [
      { key: 'evAdoption', label: 'EV adoption', tons: results.contributionsByPolicy.evAdoption ?? 0 },
      { key: 'vmtReduction', label: 'VMT reduction', tons: results.contributionsByPolicy.vmtReduction ?? 0 },
      { key: 'heatPumpAdoption', label: 'Building electrification', tons: results.contributionsByPolicy.heatPumpAdoption ?? 0 },
      { key: 'energyEfficiency', label: 'Energy efficiency', tons: results.contributionsByPolicy.energyEfficiency ?? 0 },
      { key: 'organicsDiversion', label: 'Organics diversion', tons: results.contributionsByPolicy.organicsDiversion ?? 0 },
    ];
    const localPolicyTotal = localPolicyRowsBase.reduce((sum, r) => sum + r.tons, 0);
    const localPolicyRows = localPolicyRowsBase.map((r) => ({ ...r, percent: fmtPct(r.tons, localPolicyTotal) }));

    const efficiencyContrib = efficiencyContributions2045;
    const efficiencyContribTotal =
      (efficiencyContrib?.weatherization ?? 0) +
      (efficiencyContrib?.residentialReach ?? 0) +
      (efficiencyContrib?.commercialReach ?? 0) +
      (efficiencyContrib?.demandResponse ?? 0) +
      (efficiencyContrib?.other ?? 0);
    const efficiencyEmissionAllocation = {
      weatherization:
        efficiencyContribTotal > 0
          ? (results.contributionsByPolicy.energyEfficiency ?? 0) * ((efficiencyContrib?.weatherization ?? 0) / efficiencyContribTotal)
          : 0,
      residentialReach:
        efficiencyContribTotal > 0
          ? (results.contributionsByPolicy.energyEfficiency ?? 0) * ((efficiencyContrib?.residentialReach ?? 0) / efficiencyContribTotal)
          : 0,
      commercialReach:
        efficiencyContribTotal > 0
          ? (results.contributionsByPolicy.energyEfficiency ?? 0) * ((efficiencyContrib?.commercialReach ?? 0) / efficiencyContribTotal)
          : 0,
      demandResponse:
        efficiencyContribTotal > 0
          ? (results.contributionsByPolicy.energyEfficiency ?? 0) * ((efficiencyContrib?.demandResponse ?? 0) / efficiencyContribTotal)
          : 0,
    };

    const topPolicyImpacts = [
      {
        key: 'compact',
        label: 'Compact development',
        tons: results.contributionsByPolicy.vmtReductionByComponent?.transitOrientedDevelopment ?? 0,
      },
      { key: 'weatherization', label: 'Weatherization', tons: efficiencyEmissionAllocation.weatherization },
      { key: 'ev', label: 'EV adoption', tons: results.contributionsByPolicy.evAdoption ?? 0 },
      {
        key: 'activeTransportation',
        label: 'Active transportation',
        tons: results.contributionsByPolicy.vmtReductionByComponent?.activeTransportation ?? 0,
      },
      {
        key: 'transit',
        label: 'Transit improvements',
        tons: results.contributionsByPolicy.vmtReductionByComponent?.transitImprovements ?? 0,
      },
      {
        key: 'buildingElectrification',
        label: 'Building electrification',
        tons: results.contributionsByPolicy.heatPumpAdoption ?? 0,
      },
      {
        key: 'resReach',
        label: 'Residential reach code',
        tons: efficiencyEmissionAllocation.residentialReach,
      },
      {
        key: 'comReach',
        label: 'Commercial reach code',
        tons: efficiencyEmissionAllocation.commercialReach,
      },
      {
        key: 'organics',
        label: 'Organics diversion',
        tons: results.contributionsByPolicy.organicsDiversion ?? 0,
      },
    ]
      .filter((r) => Number.isFinite(r.tons) && r.tons > 0)
      .sort((a, b) => b.tons - a.tons);

    const stateReductionTons = Math.max(0, totalReduction - localPolicyTotal);
    const localReductionTons = Math.max(0, localPolicyTotal);
    const stateReductionPercent = fmtPct(stateReductionTons, totalReduction);
    const localReductionPercent = fmtPct(localReductionTons, totalReduction);

    // Sector split for local-policy-driven reductions (aligns narrative with policy lever breakdown).
    const localSectorRows = [
      {
        key: 'transport',
        label: 'Transport',
        tons: (results.contributionsByPolicy.evAdoption ?? 0) + (results.contributionsByPolicy.vmtReduction ?? 0),
      },
      {
        key: 'buildings',
        label: 'Buildings',
        tons: (results.contributionsByPolicy.heatPumpAdoption ?? 0) + (results.contributionsByPolicy.energyEfficiency ?? 0),
      },
      {
        key: 'waste',
        label: 'Waste',
        tons: results.contributionsByPolicy.organicsDiversion ?? 0,
      },
    ];
    const localSectorTotal = localSectorRows.reduce((sum, row) => sum + row.tons, 0);
    const localSectors = localSectorRows.map((row) => ({ ...row, percent: fmtPct(row.tons, localSectorTotal) }));

    const stateNpv =
      (buildingsCostBau?.costEffectivenessMetrics?.netPresentValue ?? 0) + (transportCostBau?.netPresentValue ?? 0);
    const localNpv =
      (buildingsCostLocalIncremental?.costEffectivenessMetrics?.netPresentValue ?? 0) +
      (transportCostLocalIncremental?.netPresentValue ?? 0);

    const bSeries = buildingsCostResults?.cumulativeNetCostSeries ?? [];
    const tSeries = transportCostResults?.cumulativeNetCostSeries ?? [];
    const yearSet = new Set<number>();
    bSeries.forEach((r) => yearSet.add(r.year));
    tSeries.forEach((r) => yearSet.add(r.year));
    const years = Array.from(yearSet).sort((a, b) => a - b);
    const bByYear = new Map(bSeries.map((r) => [r.year, r.value]));
    const tByYear = new Map(tSeries.map((r) => [r.year, r.value]));

    let paybackYear: number | null = null;
    const hasInvestment = totalCapitalInvestment > 0;
    if (hasInvestment) {
      let seenPositive = false;
      for (const y of years) {
        const combined = (bByYear.get(y) ?? 0) + (tByYear.get(y) ?? 0);
        if (combined > 0) seenPositive = true;
        if (combined <= 0 && seenPositive) {
          paybackYear = y;
          break;
        }
      }
    }

    const dominantCostDriver = Math.abs(transportNpv) >= Math.abs(buildingsNpv) ? 'transport' : 'buildings';

    const headline = netSavingsNpv >= 0 ? 'Net savings' : 'Net cost';
    const headlineValue = currencyShort(Math.abs(netSavingsNpv));
    const costPerTonLabel =
      costPerTon == null ? 'Not available' : costPerTon <= 0 ? 'Cost-saving' : 'Cost-incurring';
    const paybackInterpretation =
      paybackYear == null
        ? 'No modeled payback by 2045.'
        : `Modeled payback occurs in ${paybackYear}.`;

    const driverExplanation =
      dominantCostDriver === 'transport'
        ? `Transport is the dominant financial driver (${currencyShort(transportNpv)} NPV vs ${currencyShort(buildingsNpv)} for buildings).`
        : `Buildings are the dominant financial driver (${currencyShort(buildingsNpv)} NPV vs ${currencyShort(transportNpv)} for transport).`;

    const capexVsSavingsExplanation =
      netSavingsNpv >= 0
        ? `Operating savings outweigh total capital investment (${currencyShort(totalCapitalInvestment)}), resulting in a positive NPV.`
        : `Total capital investment (${currencyShort(totalCapitalInvestment)}) outweighs operating savings over the analysis period, resulting in a net cost.`;

    const dominantSector = localSectors.slice().sort((a, b) => b.tons - a.tons)[0]?.label ?? 'Transport';
    const transportDominanceExplanation =
      dominantSector === 'Transport'
        ? 'Transport is the top local policy reduction sector because EV adoption and VMT levers contribute the largest share of local policy-driven reductions.'
        : 'Transport is not the top local policy reduction sector in this scenario; buildings or waste levers are currently contributing more of local policy-driven reductions.';

    const buildingsCostDynamicsExplanation =
      buildingsNpv < 0
        ? 'Buildings show net cost because upfront retrofits/electrification investments are large relative to modeled operating savings over the period.'
        : 'Buildings show net savings because operating bill reductions and avoided fuel costs are large enough to offset upfront investments.';

    const recommendationNarrative = [
      `For ${location}, prioritize the highest-impact levers first: ${topPolicyImpacts.slice(0, 3).map((p) => p.label.toLowerCase()).join(', ') || 'core transport and buildings measures'}. Pair these with implementation sequencing that captures early operating savings while pacing capital deployment.`,
      `Use transportation demand and electrification policies to secure near-term tonnage reductions, then scale building efficiency/electrification where co-benefits are strongest. Track progress using sector-level reductions and cost-per-ton, and revisit BAU assumptions (EMFAC, buildings pathway, grid clean year, and waste diversion) annually to keep strategy aligned with observed trends.`,
    ];
    const electricityPriceUsed = policyCostAssumptions.electricityPricePerKwh;
    const gasPricePerTherm = policyCostAssumptions.naturalGasPricePerTherm;
    const kwhPerThermEquivalent = 29.3 / Math.max(0.1, buildingsCop);
    const gasEquivalentPricePerKwh = gasPricePerTherm / kwhPerThermEquivalent;
    const breakEvenElectricityPrice = gasPricePerTherm / (29.3 / Math.max(0.1, buildingsCop));
    const billImpactDetail: BuildingBillImpactDetail | null =
      buildingsCostResults?.buildingBillImpactDetail ?? null;
    const annualBillImpact =
      billImpactDetail?.citywideTotalAnnualBillImpact ??
      (buildingsCostResults?.debug?.year2045?.policyBill ?? 0) -
        (buildingsCostResults?.debug?.year2045?.bauBill ?? 0);
    const monthlyBillImpact = annualBillImpact / 12;
    const cumulativeBillImpact =
      (buildingsCostResults?.policyBillByYear?.reduce((sum, row) => sum + row.value, 0) ?? 0) -
      (buildingsCostResults?.bauBillByYear?.reduce((sum, row) => sum + row.value, 0) ?? 0);
    if (typeof process !== 'undefined' && process.env.NODE_ENV === 'development' && billImpactDetail) {
      console.log('[Building electrification bill impact]', billImpactDetail);
    }
    const electrificationOperatingCostNarrative =
      electricityPriceUsed > breakEvenElectricityPrice
        ? `Under current prices, building electrification reduces emissions but can increase operating bills because electricity (${formatRate(electricityPriceUsed)}/kWh) is above the break-even price (${formatRate(breakEvenElectricityPrice)}/kWh at COP ${buildingsCop.toFixed(1)}).`
        : `Under current prices, building electrification reduces emissions and tends to reduce operating bills because electricity (${formatRate(electricityPriceUsed)}/kWh) is below the break-even price (${formatRate(breakEvenElectricityPrice)}/kWh at COP ${buildingsCop.toFixed(1)}).`;
    const capexVsOpexNarrative =
      'Interpretation note: capital cost is upfront investment in equipment/building upgrades, while operating cost is annual utility/fuel spending. Both drive total NPV.';
    const rateStructureNarrative =
      'Policy insight: utility rate structure (electricity and gas prices, fixed charges, and time-of-use design) strongly affects building electrification economics and payback.';

    return {
      netSavingsNpv,
      costPerTon,
      paybackYear,
      totalCapitalInvestment,
      dominantCostDriver,
      headline,
      headlineValue,
      costPerTonLabel,
      paybackInterpretation,
      driverExplanation,
      capexVsSavingsExplanation,
      sectors,
      localSectors,
      localPolicyRows,
      topPolicyImpacts,
      stateLocal: {
        stateReductionTons,
        stateReductionPercent,
        localReductionTons,
        localReductionPercent,
        stateNpv,
        localNpv,
      },
      narratives: {
        transportDominanceExplanation,
        buildingsCostDynamicsExplanation,
        electrificationOperatingCostNarrative,
        capexVsOpexNarrative,
        rateStructureNarrative,
        recommendationNarrative,
      },
      annualEnergyBillImpact: {
        annualOperatingSavings2045,
        cumulativeOperatingSavings,
      },
      buildingElectrificationBillImpact: {
        /** Citywide total annual bill impact (2045): residential + commercial modeled buildings. */
        citywideTotalAnnualBillImpact: annualBillImpact,
        monthlyCitywideTotalBillImpact: monthlyBillImpact,
        cumulativeBillImpact,
        billImpactDetail,
        /** @deprecated Use citywideTotalAnnualBillImpact — same value. */
        annualBillImpact,
        /** @deprecated Use monthlyCitywideTotalBillImpact — same value. */
        monthlyBillImpact,
      },
      energyPriceDiagnostics: {
        electricityPricePerKwh: electricityPriceUsed,
        gasPricePerTherm,
        gasEquivalentPricePerKwh,
        kwhPerThermEquivalent,
        priceSource: electricityPriceContext.source,
      },
      breakEvenElectricityPrice,
      actualElectricityPrice: electricityPriceUsed,
      bauAssumptions: {
        transport: `EMFAC${emfacVersion} baseline`,
        buildings: `${buildingsBauScenario} pathway, RNG ${Math.round(buildingsRngPercent)}%, COP ${buildingsCop.toFixed(1)}`,
        electricity: `Grid clean electricity year ${Math.round((scenario as { gridCleanElectricityYear?: number }).gridCleanElectricityYear ?? 2045)}`,
        waste: `Organics diversion target ${Math.round((scenario as { organicsDiversionPercent?: number }).organicsDiversionPercent ?? 50)}%`,
      },
    };
  }, [
    results,
    location,
    scenario,
    emfacVersion,
    buildingsBauScenario,
    buildingsRngPercent,
    buildingsCop,
    buildingsCostResults,
    transportCostResults,
    buildingsCostBau,
    transportCostBau,
    buildingsCostLocalIncremental,
    transportCostLocalIncremental,
    efficiencyContributions2045,
    electricityPriceContext,
    policyCostAssumptions,
  ]);

  // Expose current policy scenario context for structured PDF/Word export path.
  useEffect(() => {
    if (typeof window === 'undefined' || !results) return;
    const payload: ScenarioReportInput = {
      scenario,
      results,
      costResults: {
        buildings: buildingsCostResults,
        transport: transportCostResults,
      },
      breakdowns: {
        stateReduction: decisionSummary?.stateLocal.stateReductionTons,
        localReduction: decisionSummary?.stateLocal.localReductionTons,
        stateCost: decisionSummary?.stateLocal.stateNpv,
        localCost: decisionSummary?.stateLocal.localNpv,
      },
    };
    (window as Window & { __CLIMATEPLANS_SCENARIO_EXPORT_CONTEXT__?: ScenarioReportInput }).__CLIMATEPLANS_SCENARIO_EXPORT_CONTEXT__ = payload;
  }, [scenario, results, buildingsCostResults, transportCostResults, decisionSummary]);

  return (
    <div className="policy-impact-tool">
      {error && (
        <div className="policy-tool-error">
          <p>{error}</p>
        </div>
      )}

      <div className="policy-tool-content">
        {/* GHG Forecast: clean layout with chart left, metrics right */}
        <section className="policy-main-forecast">
          <div className="policy-main-forecast-header">
            <h2 className="section-title">GHG Forecast - {location}</h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                <label htmlFor="emfac-version-select" style={{ fontSize: '0.8125rem', color: '#6b7280', fontWeight: 500 }}>
                  EMFAC Version:
                </label>
                <select
                  id="emfac-version-select"
                  value={emfacVersion}
                  onChange={(e) => {
                    setEmfacVersion(e.target.value as '2021' | '2025');
                    import('../../services/policyBaselineCache').then(({ clearBaselineCache }) => {
                      clearBaselineCache();
                    });
                  }}
                  style={{
                    padding: '0.35rem 0.65rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '0.8125rem',
                    backgroundColor: 'white',
                    cursor: 'pointer',
                    fontWeight: 500,
                    color: '#1f2937'
                  }}
                >
                  <option value="2025">EMFAC2025</option>
                  <option value="2021">EMFAC2021</option>
                </select>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                <label htmlFor="buildings-bau-select" style={{ fontSize: '0.8125rem', color: '#6b7280', fontWeight: 500 }}>
                  Buildings BAU:
                </label>
                <select
                  id="buildings-bau-select"
                  value={buildingsBauScenario}
                  onChange={(e) => {
                    setBuildingsBauScenario(e.target.value as 'conservative' | 'current_policy' | 'accelerated');
                    import('../../services/policyBaselineCache').then(({ clearBaselineCache }) => {
                      clearBaselineCache();
                    });
                  }}
                  style={{
                    padding: '0.35rem 0.65rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    fontSize: '0.8125rem',
                    backgroundColor: 'white',
                    cursor: 'pointer',
                    fontWeight: 500,
                    color: '#1f2937'
                  }}
                  title="Buildings BAU electrification pathway only (stock turnover, sales curves). RNG share is not set here; set it under Building Electrification → Critical Assumptions."
                >
                  <option value="conservative">Conservative</option>
                  <option value="current_policy">Current policy</option>
                  <option value="accelerated">Accelerated</option>
                </select>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <button
                  type="button"
                  onClick={handleSaveScenario}
                  disabled={savingScenario || !user}
                  style={{
                    padding: '0.35rem 0.65rem',
                    border: '1px solid #d1d5db',
                    borderRadius: 6,
                    fontSize: '0.8125rem',
                    backgroundColor: 'white',
                    cursor: savingScenario ? 'wait' : 'pointer',
                    fontWeight: 500,
                    color: '#1f2937',
                  }}
                  title={
                    user
                      ? 'Saves this scenario to your account in the database and adds scenarioId to the URL for your own reload.'
                      : 'Sign in to save scenarios to your account.'
                  }
                >
                  {savingScenario ? 'Saving…' : 'Save Scenario'}
                </button>
                <button
                  type="button"
                  onClick={handleCopyShareableLink}
                  style={{
                    padding: '0.35rem 0.65rem',
                    border: '1px solid #d1d5db',
                    borderRadius: 6,
                    fontSize: '0.8125rem',
                    backgroundColor: 'white',
                    cursor: 'pointer',
                    fontWeight: 500,
                    color: '#1f2937',
                  }}
                  title="Copies a public shareable URL with explicit parameters only (scenarioId removed), so anyone can open it."
                >
                  {copyLinkStatus === 'success' ? 'Copied!' : copyLinkStatus === 'error' ? 'Copy failed' : 'Copy Shareable Link'}
                </button>
              </div>
              {activeChart === 'stacked' && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                  <span style={{ fontSize: '0.8125rem', color: '#6b7280' }}>Target:</span>
                  <select
                    value={targetReductionPercent}
                    onChange={(e) => setTargetReductionPercent(Number(e.target.value))}
                    style={{
                      padding: '0.35rem 0.5rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '4px',
                      fontSize: '0.8125rem',
                      backgroundColor: 'white',
                      cursor: 'pointer'
                    }}
                  >
                    {[0, 40, 50, 60, 70, 80, 90, 100].map(val => (
                      <option key={val} value={val}>{val}%</option>
                    ))}
                  </select>
                  <span style={{ fontSize: '0.8125rem', color: '#6b7280' }}>below</span>
                  <select
                    value={targetBaselineYear}
                    onChange={(e) => setTargetBaselineYear(Number(e.target.value))}
                    style={{
                      padding: '0.35rem 0.5rem',
                      border: '1px solid #d1d5db',
                      borderRadius: '4px',
                      fontSize: '0.8125rem',
                      backgroundColor: 'white',
                      cursor: 'pointer'
                    }}
                  >
                    {[2005, 2010, 2015, 2020, 2023].map(year => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                  <span style={{ fontSize: '0.8125rem', color: '#6b7280' }}>levels</span>
                </div>
              )}
              <div className="chart-type-buttons">
              <button
                className={`chart-type-button ${activeChart === 'stacked' ? 'active' : ''}`}
                onClick={() => setActiveChart('stacked')}
              >
                Stacked
              </button>
              <button
                className={`chart-type-button ${activeChart === 'line' ? 'active' : ''}`}
                onClick={() => setActiveChart('line')}
              >
                Line Chart
              </button>
              <button
                className={`chart-type-button ${activeChart === 'contributions' ? 'active' : ''}`}
                onClick={() => setActiveChart('contributions')}
              >
                Policies
              </button>
              </div>
            </div>
          </div>

          <div className="policy-main-forecast-layout">
            {results && (
              <div className="policy-main-metrics">
                <div className="impact-metric-card baseline">
                  <div className="metric-label">Current Baseline</div>
                  <div className="metric-value">
                    <span className="metric-number">
                      {(stackedTotals?.baselineTotal ?? results.baseline.totalGHG).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    </span>
                    <span className="metric-unit">MT CO₂e</span>
                  </div>
                </div>

                <div className="impact-metric-card projected">
                  <div className="metric-label">Projected with Policies</div>
                  <div className="metric-value">
                    <span className="metric-number">
                      {(stackedTotals?.projectedWithPoliciesTotal ?? results.projected.totalGHG).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    </span>
                    <span className="metric-unit">MT CO₂e</span>
                  </div>
                </div>

                <div className="impact-metric-card reduction">
                  <div className="metric-label">Total Reduction</div>
                  <div className="metric-value reduction-value">
                    <span className="metric-number">
                      {(stackedTotals?.totalReduction ?? results.reductions.totalReduction).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    </span>
                    <span className="metric-unit">MT CO₂e</span>
                  </div>
                </div>

                <div className="impact-metric-card percentage">
                  <div className="metric-label">Percent Reduction</div>
                  <div className="metric-value reduction-value">
                    <span className="metric-number">
                      {(stackedTotals?.percentReduction ?? results.reductions.percentReduction).toFixed(1)}%
                    </span>
                  </div>
                </div>
              </div>
            )}

            <div className="policy-main-chart">
              <PolicyImpactChart
                results={results}
                loading={loading}
                scenario={scenario}
                location={location}
                chartType={activeChart}
                targetReductionPercent={targetReductionPercent}
                targetBaselineYear={targetBaselineYear}
                emfacVersion={emfacVersion}
                buildingsBauScenario={buildingsBauScenario}
                buildingsRngPercent={buildingsRngPercent}
                buildingsCop={buildingsCop}
                efficiencyLayerOptions={efficiencyLayerOptions}
                onTotalsChange={setStackedTotals}
                onTimeSeriesDataChange={handleTimeSeriesDataChange}
              />
            </div>
          </div>
          {/* Implementation by 2045 — policy levers and detailed policies (when selected) before financial analysis */}
          <div className="policy-intervention-areas-section">
              <InterventionAreasControl
                interventionAreas={interventionAreas}
                onChange={setInterventionAreas}
                expandedIntervention={expandedIntervention}
                onExpandedChange={setExpandedIntervention}
                onSubPolicyExpand={setExpandedSubPolicy}
                subPolicyValues={subPolicyValues}
                onSubPolicyChange={setSubPolicyValues}
                energyEfficiencySubPolicyValues={energyEfficiencySubPolicyValues}
                onEnergyEfficiencySubPolicyChange={handleEnergyEfficiencySubPolicyChange}
                parkingVMTReductionPercent={parkingVMTReductionPercent}
                computeParkingVMTReductionPercent={(priceIncreasePercent: number) =>
                  computeParkingVMTReductionPercent({
                    ...scenario.parkingPricing,
                    priceIncreasePercent,
                  })
                }
                micromobilityVMTReductionPercent={micromobilityVMTReductionPercent}
                transitVMTReductionPercent={transitVMTReductionPercent}
                vehicleElectrificationMinPercent={evMinPercent}
                onLoadScenario={handleLoadPredefinedScenario}
                onResetScenario={handleResetScenario}
              />
            </div>

          {/* Expanded Intervention Details */}
          {expandedIntervention === 'vmtReduction' && expandedSubPolicy === 'parkingPricing' && (
            <div id="parking-pricing-details" className="policy-intervention-details-section">
              <h3 className="intervention-details-title">Parking Pricing Policy</h3>
              <div className="intervention-details-content">
                <ParkingPricingPolicy
                  assumptions={{
                    ...scenario.parkingPricing,
                    priceIncreasePercent: subPolicyValues.parkingPricing,
                  }}
                  onChange={(updatedAssumptions) => {
                    // Update scenario assumptions
                    setScenario(prev => ({
                      ...prev,
                      parkingPricing: updatedAssumptions,
                    }));

                    // Keep sub-policy slider in sync
                    setSubPolicyValues(prev => ({
                      ...prev,
                      parkingPricing: updatedAssumptions.priceIncreasePercent,
                    }));

                    // Update the top-level VMT Reduction slider (excludes Compact Development; model net statewide drives its impact)
                    const computedParking = computeParkingVMTReductionPercent(updatedAssumptions);
                    const sumOfPolicyReductions =
                      (subPolicyValues.activeTransportation || 0) +
                      transitVMTReductionPercent +
                      computedParking +
                      micromobilityVMTReductionPercent;
                    setInterventionAreas(prev => ({
                      ...prev,
                      vmtReduction: Math.max(0, Math.min(20, sumOfPolicyReductions + (subPolicyValues.otherInitiatives || 0))),
                    }));
                  }}
                  currentVMT={results?.baseline.vmt || 0}
                  projectedBAUVMT={results?.baseline.vmt || 0}
                  location={location}
                  geofips={geofips}
                />
              </div>
            </div>
          )}

          {expandedIntervention === 'vmtReduction' && expandedSubPolicy === 'transitOrientedDevelopment' && (
            <div id="compact-development-details" className="policy-intervention-details-section" style={{ marginTop: '32px' }}>
              <h3 className="intervention-details-title">Compact Development</h3>
              <div className="intervention-details-content">
                <p style={{ marginBottom: 12 }}>
                  Compact Development is primarily a land use and housing-growth policy whose emissions effects occur through lower driving and denser urban form.
                </p>

                <h4 style={{ fontSize: '0.95rem', marginTop: 16, marginBottom: 6 }}>What this policy does</h4>
                <p>
                  Shifts housing growth into lower-VMT cities and compact locations. The model assumes cities with lower VMT per capita than the state average can absorb more growth. It estimates: added emissions from new homes; citywide density-related reductions; avoided out-of-boundary emissions from building in lower-VMT places; and net local and statewide impacts.
                </p>

                <h4 style={{ fontSize: '0.95rem', marginTop: 16, marginBottom: 6 }}>Key assumptions</h4>
                <ul style={{ marginTop: 4, marginBottom: 8, paddingLeft: 20 }}>
                  <li><strong>Compact-home emissions:</strong> The app uses a <em>location-specific</em> method: new units are assumed to generate driving (and emissions) in line with this location&apos;s VMT per capita relative to the state average (from the same LDV VMT and population data as the Transportation page).</li>
                  <li><strong>Density effect:</strong> Elasticity = -0.22; applied to full city LDV emissions (citywide, stock-based).</li>
                  <li><strong>1:1 displacement:</strong> Each new home built here replaces one that would have been built elsewhere in California (required for out-of-boundary savings).</li>
                  <li><strong>Accounting:</strong> {compactDevelopmentSeries?.mode === 'workbook_exact'
                    ? 'Workbook exact — all policy new units counted in avoided and added terms.'
                    : 'Incremental baseline — only units above BAU counted in avoided and added terms.'}
                  </li>
                </ul>

                <h4 style={{ fontSize: '0.95rem', marginTop: 16, marginBottom: 6 }}>Percent growth per year (same as main slider)</h4>
                <p style={{ marginBottom: 8 }}>
                  This slider is the <em>same</em> control as the Compact Development slider under VMT policies above. Moving either one updates the other. It sets the annual policy housing growth rate (0–3%). The headline GHG forecast uses <em>net statewide savings</em> from this model when available (see Impact on forecast below).
                </p>
                <div className="policy-main-control-section" style={{ marginTop: 8, marginBottom: 12 }}>
                  <div className="policy-main-label">Percent growth per year (0–3%)</div>
                  <div className="policy-main-slider-container">
                    <input
                      type="range"
                      min={0}
                      max={GROWTH_RATE_PERCENT_MAX}
                      step={0.1}
                      value={subPolicyValues.transitOrientedDevelopment ?? compactDevelopmentSeries?.defaultGrowthRatePercent ?? 0}
                      onChange={(e) => setSubPolicyValues((prev) => ({ ...prev, transitOrientedDevelopment: Number(e.target.value) }))}
                      className="policy-main-slider"
                    />
                    <div className="policy-main-value">
                      {(subPolicyValues.transitOrientedDevelopment ?? compactDevelopmentSeries?.defaultGrowthRatePercent ?? 0).toFixed(1)}%
                    </div>
                  </div>
                  <p style={{ marginTop: 6, fontSize: '0.8rem', color: '#6b7280' }}>
                    {compactDevelopmentSeries?.bauGrowthPercent != null && compactDevelopmentSeries?.vmtPercentBelowState != null ? (
                      <>Default = {compactDevelopmentSeries.bauGrowthPercent}% (to keep with population/BAU growth) + {(compactDevelopmentSeries.vmtPercentBelowState / 100).toFixed(2)}% ({compactDevelopmentSeries.vmtPercentBelowState}% below state average) = {(compactDevelopmentSeries.defaultGrowthRatePercent ?? 0).toFixed(1)}%. This growth rate does not move the VMT Reduction slider; the headline forecast uses <em>net statewide</em> changes from the model.</>
                    ) : (
                      <>Default = (% to keep with population/BAU growth) + (% below state average) for this location. This growth rate does not move the VMT Reduction slider; the headline forecast uses <em>net statewide</em> changes from the model.</>
                    )}
                  </p>
                </div>

                <h4 style={{ fontSize: '0.95rem', marginTop: 16, marginBottom: 6 }}>Homes in or near downtown</h4>
                <p style={{ marginBottom: 8 }}>
                  Assume a share of homes are built in or near downtown (within half the distance to downtown than typical). These homes generate 29% fewer VMT than average; the slider modifies the forecast accordingly.
                </p>
                <div className="policy-main-control-section" style={{ marginTop: 8, marginBottom: 12 }}>
                  <div className="policy-main-label">Homes in or near downtown (%)</div>
                  <div className="policy-main-slider-container">
                    <input
                      type="range"
                      min={0}
                      max={100}
                      step={1}
                      value={subPolicyValues.downtownHomesPercent ?? 70}
                      onChange={(e) => setSubPolicyValues((prev) => ({ ...prev, downtownHomesPercent: Number(e.target.value) }))}
                      className="policy-main-slider"
                    />
                    <div className="policy-main-value">
                      {subPolicyValues.downtownHomesPercent ?? 70}%
                    </div>
                  </div>
                  <p style={{ marginTop: 6, fontSize: '0.8rem', color: '#6b7280' }}>
                    Default 70%. Effective VMT reduction from this option = 29% × this share (e.g. 70% → ~20% reduction).
                  </p>
                </div>

                <h4 style={{ fontSize: '0.95rem', marginTop: 16, marginBottom: 6 }}>Interpretation</h4>
                <p>
                  Local emissions may rise (more homes, more travel). Statewide emissions can still fall because households in compact, lower-VMT cities drive less than average Californians. Charts report both annual and cumulative net local and net statewide impacts from the same computed series.
                </p>

                <h4 style={{ fontSize: '0.95rem', marginTop: 16, marginBottom: 6 }}>Limitations</h4>
                <p>
                  Policy in practice: update zoning and encourage compact, mixed-use housing near downtown and transit. This typically increases community emissions in absolute terms while reducing statewide VMT and GHG (Scope 4). Long term, density can lower community-wide emissions via supportive infrastructure and alternative travel. Results depend on the 1:1 displacement and citywide density assumptions.
                </p>

                {/* Recommendation: VMT below state average AND density > 3000 */}
                {compactDevelopmentSeries && (
                  <div
                    className="policy-compact-recommendation"
                    style={{
                      marginTop: 16,
                      padding: '12px 14px',
                      background: compactDevelopmentSeries.recommendedForLocation ? '#f0fdf4' : '#fef2f2',
                      border: compactDevelopmentSeries.recommendedForLocation ? '1px solid #86efac' : '1px solid #fca5a5',
                      borderRadius: 8,
                    }}
                  >
                    {compactDevelopmentSeries.recommendedForLocation ? (
                      <>
                        <strong>Recommended for this location.</strong>{' '}
                        {compactDevelopmentSeries.densityDataMissing ? (
                          <>VMT per capita {compactDevelopmentSeries.vmtPercentBelowState > 0 ? `${compactDevelopmentSeries.vmtPercentBelowState}% below state average. ` : ''}Density data not available for this geography; policy is generally best in denser areas.</>
                        ) : (
                          <>VMT per capita {compactDevelopmentSeries.vmtPercentBelowState > 0 ? `${compactDevelopmentSeries.vmtPercentBelowState}% below state average` : 'below state average'} and density ≥ {COMPACT_DEVELOPMENT_MIN_DENSITY.toLocaleString()} per sq mi.
                            {compactDevelopmentSeries.populationDensityPerSqMi != null && (
                              <> ({compactDevelopmentSeries.populationDensityPerSqMi.toLocaleString(undefined, { maximumFractionDigits: 0 })} per sq mi.)</>
                            )}
                          </>
                        )}
                      </>
                    ) : (
                      <>
                        <strong>Not recommended.</strong> VMT per capita {compactDevelopmentSeries.vmtPercentAboveState > 0 ? `${compactDevelopmentSeries.vmtPercentAboveState}% above state average` : 'at or above state average'}, or density is &lt;{COMPACT_DEVELOPMENT_MIN_DENSITY.toLocaleString()} per sq mi (or unknown). Best applied in lower-VMT, denser areas.
                      </>
                    )}
                  </div>
                )}

                {results?.baseline?.lightDutyGHG != null && (
                  <p style={{ marginTop: 16, fontSize: '0.9rem' }}>
                    <strong>Impact on forecast.</strong> The headline GHG forecast uses <em>net statewide savings</em> from this model when data are available (includes out-of-boundary savings). Compact Development LDV reduction in the policy year ≈ {Math.round((results.contributionsByPolicy?.vmtReductionByComponent?.transitOrientedDevelopment ?? 0)).toLocaleString()} MT CO₂e. Charts and summary below use the same computed series (annual and cumulative).
                  </p>
                )}

                <details style={{ marginTop: 12, fontSize: '0.85rem' }}>
                  <summary style={{ cursor: 'pointer', fontWeight: 600 }}>How calculated</summary>
                  <ul style={{ marginTop: 6, paddingLeft: 20 }}>
                    <li>Growth rate = (BAU growth % + % below state average) / 100, or slider override. Savings = difference between BAU and with policy.</li>
                    <li>New units (policy) = growth rate × housing stock; additional = policy − BAU.</li>
                    <li>Compact-home emissions = location VMT/cap ÷ state VMT/cap × state GHG per home (when city &lt; state).</li>
                    <li>Out-of-boundary savings = additional units × (state GHG per home − compact per home).</li>
                    <li>Density savings = − city LDV × growth rate × 0.22. Added local = additional units × compact per home.</li>
                    <li>Net local = density savings − added local; net statewide = out-of-boundary + density savings − added local. Cumulatives = sum of annual values.</li>
                  </ul>
                </details>

                {compactDevelopmentSeries && compactDevelopmentSeries.rows.length > 0 && (() => {
                  const series = compactDevelopmentSeries;
                  const rows = series.rows;
                  const last = rows[rows.length - 1];
                  const lastYear = last.year;
                  const numYears = rows.length;
                  const avgNewUnitsBau = numYears > 0 ? rows.reduce((s, r) => s + r.newUnitsBau, 0) / numYears : 0;
                  const avgHousingStock = numYears > 0 ? rows.reduce((s, r) => s + r.housingUnitsBau, 0) / numYears : 0;
                  const hasCityData = avgHousingStock >= 1 || last.cityLdvGhg > 0;
                  const pctStock = avgHousingStock > 0 ? (avgNewUnitsBau / avgHousingStock) * 100 : 0;
                  const avgNewUnitsPolicy = numYears > 0 ? rows.reduce((s, r) => s + r.newUnitsPolicy, 0) / numYears : 0;
                  const projected20YrLocal = numYears > 0 ? (last.netLocalCumulative / numYears) * 20 : 0;
                  const projected20YrState = numYears > 0 ? (last.netStatewideCumulative / numYears) * 20 : 0;
                  const vmtPctBelowState = Math.round(last.vmtBelowState * 100);
                  const currentBuildPhrase = Math.round(avgNewUnitsBau) >= 10 ? Math.round(avgNewUnitsBau).toLocaleString() : avgNewUnitsBau.toFixed(1);
                  const compactTargetPhrase = Math.round(avgNewUnitsPolicy) >= 10 ? Math.round(avgNewUnitsPolicy).toLocaleString() : avgNewUnitsPolicy.toFixed(1);
                  const localTons = Math.abs(Math.round(projected20YrLocal));
                  const stateTons = Math.abs(Math.round(projected20YrState));
                  // netLocal = local added − density avoided; > 0 means local emissions increase
                  const localIncrease = projected20YrLocal > 0;
                  // netStatewide = statewide avoided + local added − density avoided; < 0 means statewide reduction
                  const stateReduction = projected20YrState < 0;
                  const driveLessPhrase = vmtPctBelowState > 0
                    ? `since ${location} residents drive ${vmtPctBelowState}% less than average Californians`
                    : `; ${location} residents drive at or above the state average`;
                  const localSentence = localIncrease
                    ? `While this will lead to a net increase in local GHG emissions of ${localTons.toLocaleString()} tons of CO₂e over the next 20 years, `
                    : `This leads to a net decrease in local GHG emissions of ${localTons.toLocaleString()} tons of CO₂e over the next 20 years. `;
                  const stateSentence = stateReduction
                    ? `statewide emissions will be reduced by ${stateTons.toLocaleString()} tons ${driveLessPhrase}.`
                    : `statewide emissions would increase by ${stateTons.toLocaleString()} tons ${driveLessPhrase}.`;
                  if (!hasCityData) {
                    return (
                      <div style={{ marginTop: 16 }}>
                        <p style={{ fontSize: '0.9375rem', lineHeight: 1.5 }}>
                          <strong>Summary for {location}:</strong> City-level housing or emissions data is not available for this location in the compact development model, so the summary cannot be computed. The chart below may show zeros. If you expect data for this city, it may be stored under a different geography type or geofips; try another location or ensure data has been loaded for this city.
                        </p>
                      </div>
                    );
                  }
                  return (
                    <div style={{ marginTop: 16 }}>
                      <p style={{ fontSize: '0.9375rem', lineHeight: 1.5 }}>
                        <strong>Summary for {location}:</strong> {location} currently builds about {currentBuildPhrase} new homes per year, or {pctStock.toFixed(2)}% of the current housing stock. Meeting compact development goals would require {compactTargetPhrase} homes per year over the next 20 years. {localSentence}{stateSentence}
                      </p>
                      <p style={{ marginTop: 8, fontSize: '0.875rem', color: '#6b7280' }}>
                        These 20-year totals match the <strong>Cumulative local, statewide and net</strong> chart below (values at the end of the period). Annual charts can show negative (red) years early on; the cumulative chart shows the net effect over time.
                      </p>
                      <p style={{ marginTop: 4, fontSize: '0.875rem', color: '#6b7280' }}>
                        Modeled period: through {lastYear}. Years after the last data year use projected BAU growth and last-known city/state emissions and VMT.
                      </p>
                    </div>
                  );
                })()}

                <CompactDevelopmentCharts
                  location={location}
                  series={compactDevelopmentSeries}
                  loading={compactDevelopmentLoading}
                />
              </div>
            </div>
          )}

          {expandedIntervention === 'vmtReduction' && expandedSubPolicy === 'micromobility' && (
            <div id="micromobility-details" className="policy-intervention-details-section" style={{ marginTop: '32px' }}>
              <h3 className="intervention-details-title">Expand Micromobility</h3>
              <div className="intervention-details-content">
                  {/* Top full-width control mirroring the menu slider */}
                  <div className="policy-main-control-section" style={{ marginBottom: 16 }}>
                    <div className="policy-main-label">Expand micromobility by</div>
                    <div className="policy-main-slider-container">
                      <input
                        type="range"
                        min={0}
                        max={200}
                        step={5}
                        value={subPolicyValues.micromobility}
                        onChange={(e) => setSubPolicyValues(prev => ({ ...prev, micromobility: Number(e.target.value) }))}
                        className="policy-main-slider"
                      />
                      <div className="policy-main-value">{subPolicyValues.micromobility}%</div>
                    </div>
                  </div>
                  <p>
                    This measure estimates reduced GHG emissions from trips shifted to shared e-bikes and e-scooters and
                    subtracts added electricity emissions. Default assumptions reflect findings that roughly one-third of
                    micromobility trips displace motor vehicle trips. Users can scale program intensity with the slider in the menu.
                  </p>

                  {micromobilityStats && (
                    <div className="policy-results-grid">
                      <div className="result-item">
                        <span className="result-label">Population:</span>
                        <span className="result-value">{micromobilityStats.population.toLocaleString()}</span>
                      </div>
                      <div className="result-item">
                        <span className="result-label">Program Scale:</span>
                        <span className="result-value">{micromobilityStats.scalePercent}%</span>
                      </div>
                      <div className="result-item">
                        <span className="result-label">Trips per Resident:</span>
                        <span className="result-value">{micromobilityStats.tripsPerResident}</span>
                      </div>
                      <div className="result-item">
                        <span className="result-label">Total Program Trips:</span>
                        <span className="result-value">{micromobilityStats.totalProgramTrips.toLocaleString()}</span>
                      </div>
                      <div className="result-item">
                        <span className="result-label">Annual Bike Miles:</span>
                        <span className="result-value">{micromobilityStats.bikeMiles.toLocaleString()}</span>
                      </div>
                      <div className="result-item">
                        <span className="result-label">Annual Scooter Miles:</span>
                        <span className="result-value">{micromobilityStats.scooterMiles.toLocaleString()}</span>
                      </div>
                      <div className="result-item highlight">
                        <span className="result-label">VMT Reduced:</span>
                        <span className="result-value">{micromobilityStats.displacedCarMiles.toLocaleString()} miles</span>
                      </div>
                      <div className="result-item">
                        <span className="result-label">EV Bike+Scooter Electricity:</span>
                        <span className="result-value">{micromobilityStats.electricityKwh.toLocaleString()} kWh</span>
                      </div>
                      <div className="result-item">
                        <span className="result-label">Tons Avoided from Cars:</span>
                        <span className="result-value">{micromobilityStats.avoidedCarTons.toLocaleString()}</span>
                      </div>
                      <div className="result-item">
                        <span className="result-label">Tons Added from Electricity:</span>
                        <span className="result-value">{micromobilityStats.electricityTons.toLocaleString()}</span>
                      </div>
                      <div className="result-item highlight">
                        <span className="result-label">Net GHG Reduction:</span>
                        <span className="result-value">{micromobilityStats.netTons.toLocaleString()} MT CO₂e</span>
                      </div>
                    </div>
                  )}

                  {!micromobilityStats && (
                    <p style={{ opacity: 0.7 }}>Loading micromobility statistics…</p>
                  )}
                </div>
              </div>
            )}

          {expandedIntervention === 'vehicleElectrification' && (
            <div id="vehicle-electrification-details" className="policy-intervention-details-section">
              <h3 className="intervention-details-title">Vehicle Electrification Policy</h3>
              <div className="intervention-details-content">
                <VehicleElectrificationPolicy
                  emfacVersion={emfacVersion}
                  currentEVPercent={results?.baseline.currentEVPercent || 0}
                  targetEVPercent={interventionAreas.vehicleElectrification}
                  location={location}
                  results={results}
                />
              </div>
            </div>
          )}

          {expandedIntervention === 'buildingsElectrification' && (
            <div id="building-electrification-details" className="policy-intervention-details-section">
              <h3 className="intervention-details-title">Building Electrification Policy</h3>
              <div className="intervention-details-content">
                <BuildingElectrificationPolicy
                  additionalElectrificationPercent={interventionAreas.buildingsElectrification ?? scenario.heatPumpAdoptionPercent ?? 0}
                  results={results}
                  bauTherms2045={results?.baseline.bauTherms2045}
                  bauKwh2045={results?.baseline.bauKwh2045}
                  rngShare2045={results?.baseline.rngShare2045}
                  bauElectrifiedShare2045={results?.baseline.bauElectrifiedShare2045}
                  bauElectrifiedShareResidential2045={results?.baseline.bauElectrifiedShareResidential2045}
                  bauElectrifiedShareCommercial2045={results?.baseline.bauElectrifiedShareCommercial2045}
                  location={location}
                  buildingsBauScenario={buildingsBauScenario}
                  buildingsRngPercent={buildingsRngPercent}
                  onBuildingsRngChange={setBuildingsRngPercent}
                  buildingsCop={buildingsCop}
                  onBuildingsCopChange={setBuildingsCop}
                  buildingEmissionsTimeSeries={buildingEmissionsTimeSeries}
                  cleanElectricityYear={(scenario as { gridCleanElectricityYear?: number }).gridCleanElectricityYear ?? 2045}
                  billImpactDiagnostics={decisionSummary?.buildingElectrificationBillImpact}
                  priceDiagnostics={decisionSummary?.energyPriceDiagnostics}
                  breakEvenElectricityPricePerKwh={decisionSummary?.breakEvenElectricityPrice}
                  buildingEnergyComparisonSeries={buildingEnergyComparisonSeries}
                  heatPumpAdoptionPercent2045={
                    interventionAreas.buildingsElectrification ?? scenario.heatPumpAdoptionPercent ?? 0
                  }
                  buildingEndUseAssumptions={scenario.buildingEndUseAssumptions ?? undefined}
                  onBuildingEndUseAssumptionsChange={(next) => {
                    setScenario((prev) => ({ ...prev, buildingEndUseAssumptions: next }));
                  }}
                />
              </div>
            </div>
          )}

          {expandedIntervention === 'energyEfficiency' && (
            <div id="energy-efficiency-details" className="policy-intervention-details-section">
              <h3 className="intervention-details-title">Energy Efficiency</h3>
              <div className="intervention-details-content">
                <p style={{ marginBottom: 12 }}>
                  Local energy efficiency reduces building demand (therms and kWh) before additional electrification is applied.
                  Policies apply by segment: reach codes to new construction, weatherization to existing stock, demand response to electricity only (capped at 5%).
                </p>
                <p style={{ marginBottom: 12, fontSize: '0.9rem', color: '#6b7280' }}>
                  Order: BAU demand → efficiency layer → additional building electrification beyond BAU → RNG and grid carbon intensity.
                </p>
                <p style={{ marginBottom: 12, fontSize: '0.875rem', color: '#6b7280' }}>
                  Higher efficiency reduces total energy demand, including fossil energy available for electrification. As a result, very high efficiency can reduce the apparent impact of electrification policies.
                </p>
                <p style={{ marginBottom: 8, fontSize: '0.875rem', color: '#6b7280' }}>
                  <strong>Demand response</strong> is modeled as an annual electricity (kWh) reduction, not a full load-shape or peak-demand simulation.
                </p>
                <p style={{ marginBottom: 16, fontSize: '0.875rem', color: '#6b7280' }}>
                  <strong>Other / Unmodeled Efficiency</strong> represents miscellaneous building efficiency measures not explicitly modeled (e.g. behavior, equipment standards, other programs). In target mode it is the residual to meet the top-level goal; in policy mode it is zero.
                </p>

                <EnergyEfficiencyOverTimeChart data={efficiencyChartData} />
                <EfficiencyByPolicyChart contributions={efficiencyContributions2045} yearLabel="2045" />

                {location && /Berkeley/i.test(location) && results?.baseline && (
                  <details style={{ marginTop: 16, padding: 12, background: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0' }}>
                    <summary style={{ cursor: 'pointer', fontWeight: 600 }}>Berkeley validation</summary>
                    <div style={{ marginTop: 8, fontSize: '0.8125rem' }}>
                      <p style={{ marginBottom: 6, fontWeight: 600 }}>Existing vs new share (residential)</p>
                      <p style={{ marginBottom: 4 }}>2030: existing {efficiencySegmentShares ? (efficiencySegmentShares.resExisting2030 * 100).toFixed(1) : '—'}%, new {efficiencySegmentShares ? ((1 - efficiencySegmentShares.resExisting2030) * 100).toFixed(1) : '—'}%</p>
                      <p style={{ marginBottom: 12 }}>2045: existing {efficiencySegmentShares ? (efficiencySegmentShares.resExisting2045 * 100).toFixed(1) : '—'}%, new {efficiencySegmentShares ? ((1 - efficiencySegmentShares.resExisting2045) * 100).toFixed(1) : '—'}%</p>
                      <p style={{ marginBottom: 6, fontWeight: 600 }}>Existing vs new share (commercial)</p>
                      <p style={{ marginBottom: 4 }}>2030: existing {efficiencySegmentShares ? (efficiencySegmentShares.comExisting2030 * 100).toFixed(1) : '—'}%, new {efficiencySegmentShares ? ((1 - efficiencySegmentShares.comExisting2030) * 100).toFixed(1) : '—'}%</p>
                      <p style={{ marginBottom: 12 }}>2045: existing {efficiencySegmentShares ? (efficiencySegmentShares.comExisting2045 * 100).toFixed(1) : '—'}%, new {efficiencySegmentShares ? ((1 - efficiencySegmentShares.comExisting2045) * 100).toFixed(1) : '—'}%</p>
                      <p style={{ marginBottom: 6, fontWeight: 600 }}>BAU vs efficiency-adjusted 2045</p>
                      <p style={{ marginBottom: 4 }}>BAU therms: {(results.baseline.bauTherms2045 ?? results.baseline.naturalGasTherms ?? 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}</p>
                      <p style={{ marginBottom: 4 }}>BAU kWh: {(results.baseline.bauKwh2045 ?? results.baseline.electricityKwh ?? 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}</p>
                      {efficiencyChartData && efficiencyChartData.years.indexOf(2045) >= 0 && (
                        <>
                          <p style={{ marginBottom: 4 }}>Efficiency-adjusted therms: {(efficiencyChartData.effTherms[efficiencyChartData.years.indexOf(2045)] ?? 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}</p>
                          <p style={{ marginBottom: 12 }}>Efficiency-adjusted kWh: {(efficiencyChartData.effKwh[efficiencyChartData.years.indexOf(2045)] ?? 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}</p>
                        </>
                      )}
                      <p style={{ marginBottom: 4, fontWeight: 600 }}>Actual total efficiency % from component sliders</p>
                      <p>{(scenario as any).energyEfficiencyTargetPercent ?? (scenario as any).energyEfficiencyPercent ?? 0}%</p>
                    </div>
                  </details>
                )}
              </div>
            </div>
          )}

          {expandedIntervention === 'vmtReduction' && expandedSubPolicy === 'transitImprovements' && (
            <div id="transit-service-details" className="policy-intervention-details-section" style={{ marginTop: '32px' }}>
              <h3 className="intervention-details-title">Extend Transit Network Coverage of Miles</h3>
              <div className="intervention-details-content">
                {/* Top full-width control mirroring the menu slider */}
                <div className="policy-main-control-section" style={{ marginBottom: 16 }}>
                  <div className="policy-main-label">Expand transit service by</div>
                  <div className="policy-main-slider-container">
                    <input
                      type="range"
                      min={0}
                      max={50}
                      step={1}
                      value={subPolicyValues.transitImprovements}
                      onChange={(e) => setSubPolicyValues(prev => ({ ...prev, transitImprovements: Number(e.target.value) }))}
                      className="policy-main-slider"
                    />
                    <div className="policy-main-value">{subPolicyValues.transitImprovements}%</div>
                  </div>
                </div>

                <p>
                  This measure will expand the local transit network by either adding or modifying existing service or
                  extending operating hours near the project area. Earlier starts and later end times can accommodate
                  alternative-shift workers, encouraging transit use and reducing VMT and associated GHG emissions.
                </p>

                {(() => {
                  const percentIncrease = subPolicyValues.transitImprovements || 0; // %
                  const vmtReductionPctRounded = calculateTransitVmtReductionPercent(percentIncrease);
                  const yearsTo2045 = TRANSIT_PROJECTION_END_YEAR - TRANSIT_IMPLEMENTATION_YEAR + 1;
                  const ldvBaseline = results?.baseline.lightDutyGHG || 0;
                  const estGHGReduction2045 = Math.round(ldvBaseline * yearsTo2045 * (vmtReductionPctRounded / 100));
                  const baselineServiceMiles = transitRevenueMiles?.miles;
                  const baselineYear = transitRevenueMiles?.year;
                  const totalVmt = results?.baseline.vmt || 0;
                  const vmtReducedMiles = Math.round(totalVmt * (vmtReductionPctRounded / 100));
                  return (
                    <div>
                      <p style={{ marginTop: 8 }}>
                        <strong>Smart narrative:</strong> {location}
                        {baselineServiceMiles ? ` has ${baselineServiceMiles.toLocaleString()} transit service miles in ${baselineYear}.` : ''}
                        {` If ${location} increases transit service miles by ${percentIncrease}%, total GHG emissions from light-duty vehicles by 2045 are estimated to be reduced by ${estGHGReduction2045.toLocaleString()} tCO₂e.`}
                      </p>
                      <div className="policy-results-grid" style={{ marginTop: 12 }}>
                        <div className="result-item"><span className="result-label">Elasticity:</span><span className="result-value">{TRANSIT_ELASTICITY}</span></div>
                        <div className="result-item"><span className="result-label">Transit mode share:</span><span className="result-value">{TRANSIT_MODE_SHARE_PERCENT}%</span></div>
                        <div className="result-item"><span className="result-label">Statewide mode shift factor:</span><span className="result-value">{TRANSIT_STATEWIDE_MODE_SHIFT_PERCENT}%</span></div>
                        <div className="result-item highlight"><span className="result-label">VMT reduction in year of implementation:</span><span className="result-value">{vmtReductionPctRounded.toFixed(3)}%</span></div>
                        <div className="result-item highlight"><span className="result-label">Total VMT reduced (year of implementation):</span><span className="result-value">{vmtReducedMiles.toLocaleString()} miles</span></div>
                      </div>
                      <p style={{ marginTop: 8, opacity: 0.75 }}>
                        Sources: FHWA 2017; Handy et al. 2013.
                      </p>
                      <TransitServiceExpansionCharts
                        location={location}
                        historicalServiceMiles={transitServiceHistory}
                        percentIncrease={percentIncrease}
                        baselineServiceMiles={baselineServiceMiles}
                        baselineYear={baselineYear}
                        implementationYear={TRANSIT_IMPLEMENTATION_YEAR}
                        projectionEndYear={TRANSIT_PROJECTION_END_YEAR}
                      />
                    </div>
                  );
                })()}
              </div>
            </div>
          )}

          {decisionSummary && (
            <details
              style={{
                marginTop: 24,
                marginBottom: 16,
                padding: 16,
                border: '1px solid #e5e7eb',
                borderRadius: 10,
                background: '#f8fafc',
              }}
            >
              <summary style={{ cursor: 'pointer', fontSize: '1.05rem', fontWeight: 700 }}>Decision Summary</summary>
              <div style={{ marginTop: 10 }}>
                <p style={{ margin: '0 0 10px 0', fontSize: '0.82rem', color: '#64748b' }}>
                  All values represent emissions reductions relative to baseline.
                </p>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10 }}>
                  <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 8, padding: 10 }}>
                    <div style={{ fontSize: '0.78rem', color: '#6b7280' }}>{decisionSummary.headline}</div>
                    <div style={{ fontSize: '1.15rem', fontWeight: 700 }}>{decisionSummary.headlineValue}</div>
                  </div>
                  <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 8, padding: 10 }}>
                    <div style={{ fontSize: '0.78rem', color: '#6b7280' }}>Cost per ton</div>
                    <div style={{ fontSize: '0.98rem', fontWeight: 600 }}>
                      {decisionSummary.costPerTon != null ? `${currencyShort(decisionSummary.costPerTon)} / tCO2e` : '—'}
                    </div>
                    <div style={{ fontSize: '0.78rem', color: '#6b7280' }}>{decisionSummary.costPerTonLabel}</div>
                  </div>
                  <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 8, padding: 10 }}>
                    <div style={{ fontSize: '0.78rem', color: '#6b7280' }}>Payback</div>
                    <div style={{ fontSize: '0.98rem', fontWeight: 600 }}>
                      {decisionSummary.paybackYear != null ? decisionSummary.paybackYear : 'No payback by 2045'}
                    </div>
                    <div style={{ fontSize: '0.78rem', color: '#6b7280' }}>{decisionSummary.paybackInterpretation}</div>
                  </div>
                  <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 8, padding: 10 }}>
                    <div style={{ fontSize: '0.78rem', color: '#6b7280' }}>Total capital investment</div>
                    <div style={{ fontSize: '0.98rem', fontWeight: 600 }}>{currencyShort(decisionSummary.totalCapitalInvestment)}</div>
                  </div>
                  <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 8, padding: 10 }}>
                    <div style={{ fontSize: '0.78rem', color: '#6b7280' }}>Dominant cost driver</div>
                    <div style={{ fontSize: '0.98rem', fontWeight: 600, textTransform: 'capitalize' }}>
                      {decisionSummary.dominantCostDriver}
                    </div>
                  </div>
                </div>
                <div style={{ marginTop: 10, fontSize: '0.875rem', color: '#334155' }}>
                  <p style={{ margin: '0 0 6px 0' }}>{decisionSummary.driverExplanation}</p>
                  <p style={{ margin: 0 }}>{decisionSummary.capexVsSavingsExplanation}</p>
                </div>
                <div style={{ marginTop: 12, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 8, padding: 10 }}>
                    <div style={{ fontSize: '0.82rem', fontWeight: 600, marginBottom: 6 }}>State vs Local Emissions Reduction (tCO2e, % of total)</div>
                    <div style={{ fontSize: '0.8rem', color: '#475569' }}>
                      <div>State/background reductions: {Math.round(decisionSummary.stateLocal.stateReductionTons).toLocaleString()} tCO2e ({decisionSummary.stateLocal.stateReductionPercent.toFixed(1)}%)</div>
                      <div>Local policy reductions: {Math.round(decisionSummary.stateLocal.localReductionTons).toLocaleString()} tCO2e ({decisionSummary.stateLocal.localReductionPercent.toFixed(1)}%)</div>
                      <div>State/background NPV: {currencyShort(decisionSummary.stateLocal.stateNpv)}</div>
                      <div>Local incremental NPV: {currencyShort(decisionSummary.stateLocal.localNpv)}</div>
                    </div>
                  </div>
                  <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 8, padding: 10 }}>
                    <div style={{ fontSize: '0.82rem', fontWeight: 600, marginBottom: 6 }}>Sector Emissions Reduction (tCO2e, % of total)</div>
                    <div style={{ fontSize: '0.8rem', color: '#475569' }}>
                      {decisionSummary.sectors.map((s) => (
                        <div key={s.key}>
                          {s.label}: {Math.round(s.tons).toLocaleString()} tCO2e ({s.percent.toFixed(1)}%)
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                <div style={{ marginTop: 12, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 8, padding: 10 }}>
                    <div style={{ fontSize: '0.82rem', fontWeight: 600, marginBottom: 6 }}>Policy Lever Emissions Reduction (tCO2e, % of local)</div>
                    <div style={{ fontSize: '0.8rem', color: '#475569' }}>
                      {decisionSummary.localPolicyRows.map((p) => (
                        <div key={p.key}>
                          {p.label}: {Math.round(p.tons).toLocaleString()} tCO2e ({p.percent.toFixed(1)}%)
                        </div>
                      ))}
                    </div>
                  </div>
                  <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 8, padding: 10 }}>
                    <div style={{ fontSize: '0.82rem', fontWeight: 600, marginBottom: 6 }}>Top Individual Policy Emissions Reduction (tCO2e)</div>
                    <div style={{ fontSize: '0.8rem', color: '#475569' }}>
                      {decisionSummary.topPolicyImpacts.slice(0, 6).map((p, idx) => (
                        <div key={p.key}>
                          {idx + 1}. {p.label}: {Math.round(p.tons).toLocaleString()} tCO2e
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                <div style={{ marginTop: 12, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 8, padding: 10 }}>
                    <div style={{ fontSize: '0.82rem', fontWeight: 600, marginBottom: 6 }}>Building Electrification Bill Impact</div>
                    <div style={{ fontSize: '0.8rem', color: '#475569' }}>
                      <div>
                        Citywide total annual bill impact (2045):{' '}
                        {currencyShort(decisionSummary.buildingElectrificationBillImpact.citywideTotalAnnualBillImpact)} / year
                      </div>
                      <div style={{ fontSize: '0.76rem', color: '#64748b', marginTop: 2 }}>
                        Scope: all modeled building gas + electricity (residential + commercial).
                      </div>
                      <div style={{ fontSize: '0.74rem', color: '#64748b', marginTop: 4 }}>
                        Note: <strong>Annual Cash Flow</strong> in the cost section combines <strong>buildings utility</strong> with{' '}
                        <strong>transport fuel</strong> savings/costs—so its green “operating” bars can be positive overall even when
                        building bills rise.
                      </div>
                      <div>Monthly (citywide total): {currencyShort(decisionSummary.buildingElectrificationBillImpact.monthlyCitywideTotalBillImpact)} / month</div>
                      <div>
                        Cumulative bill change (to 2045): {currencyShort(decisionSummary.buildingElectrificationBillImpact.cumulativeBillImpact)}
                      </div>
                      {decisionSummary.buildingElectrificationBillImpact.billImpactDetail && (
                        <>
                          <div style={{ marginTop: 8, fontWeight: 600 }}>Per housing unit (2045)</div>
                          {decisionSummary.buildingElectrificationBillImpact.billImpactDetail.citywideAverageBillImpactPerHousingUnitAnnual != null && (
                            <div>
                              Spread of citywide total across all homes:{' '}
                              {currencyShort(
                                decisionSummary.buildingElectrificationBillImpact.billImpactDetail
                                  .citywideAverageBillImpactPerHousingUnitAnnual,
                              )}
                              / home-year (num{' '}
                              {currencyShort(
                                decisionSummary.buildingElectrificationBillImpact.billImpactDetail.citywidePerHousingUnit
                                  .numeratorAnnual,
                              )}{' '}
                              ÷ {decisionSummary.buildingElectrificationBillImpact.billImpactDetail.citywidePerHousingUnit.denominatorHousingUnits?.toLocaleString() ?? '—'}{' '}
                              homes)
                            </div>
                          )}
                          {decisionSummary.buildingElectrificationBillImpact.billImpactDetail.averageAcrossAllHomesAnnualBillImpact != null && (
                            <div>
                              Average across all homes (residential-attributed):{' '}
                              {currencyShort(
                                decisionSummary.buildingElectrificationBillImpact.billImpactDetail
                                  .averageAcrossAllHomesAnnualBillImpact,
                              )}
                              / home-year (num{' '}
                              {currencyShort(
                                decisionSummary.buildingElectrificationBillImpact.billImpactDetail.allHomesResidentialAttributed
                                  .numeratorAnnual,
                              )}{' '}
                              ÷ {decisionSummary.buildingElectrificationBillImpact.billImpactDetail.allHomesResidentialAttributed.denominatorHousingUnits?.toLocaleString() ?? '—'}{' '}
                              homes)
                            </div>
                          )}
                          {decisionSummary.buildingElectrificationBillImpact.billImpactDetail.averageForAffectedHomesAnnualBillImpact != null && (
                            <div>
                              Average for affected homes:{' '}
                              {currencyShort(
                                decisionSummary.buildingElectrificationBillImpact.billImpactDetail
                                  .averageForAffectedHomesAnnualBillImpact,
                              )}
                              / home-year (num{' '}
                              {currencyShort(
                                decisionSummary.buildingElectrificationBillImpact.billImpactDetail.affectedHomesResidentialAttributed
                                  .numeratorAnnual,
                              )}{' '}
                              ÷ {decisionSummary.buildingElectrificationBillImpact.billImpactDetail.affectedHomesResidentialAttributed.denominatorElectrifiedHomes.toLocaleString()}{' '}
                              electrified homes)
                            </div>
                          )}
                          <div style={{ marginTop: 6, fontSize: '0.76rem', color: '#64748b' }}>
                            Therms reduced (2045):{' '}
                            {Math.round(
                              decisionSummary.buildingElectrificationBillImpact.billImpactDetail.annualThermsReduced2045,
                            ).toLocaleString()}
                            {' · '}
                            kWh added (2045):{' '}
                            {Math.round(
                              decisionSummary.buildingElectrificationBillImpact.billImpactDetail.annualKwhAdded2045,
                            ).toLocaleString()}
                          </div>
                        </>
                      )}
                      <div style={{ marginTop: 4, fontSize: '0.76rem', color: '#64748b' }}>
                        Positive values indicate bill increases; negative values indicate bill decreases.
                      </div>
                    </div>
                  </div>
                  <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: 8, padding: 10 }}>
                    <div style={{ fontSize: '0.82rem', fontWeight: 600, marginBottom: 6 }}>Comparable Energy Price Diagnostics</div>
                    <div style={{ fontSize: '0.8rem', color: '#475569' }}>
                      <div>Electricity price: ${decisionSummary.energyPriceDiagnostics.electricityPricePerKwh.toFixed(3)} / kWh</div>
                      <div>Natural gas price: ${decisionSummary.energyPriceDiagnostics.gasPricePerTherm.toFixed(3)} / therm</div>
                      <div>Natural gas electric-equivalent price: ${decisionSummary.energyPriceDiagnostics.gasEquivalentPricePerKwh.toFixed(3)} / kWh-e</div>
                      <div>Break-even electricity price: ${decisionSummary.breakEvenElectricityPrice.toFixed(3)} / kWh</div>
                      <div style={{ marginTop: 4, fontSize: '0.76rem', color: '#64748b' }}>
                        Source: {decisionSummary.energyPriceDiagnostics.priceSource}. Formula: gas_price / (29.3 / COP), COP {buildingsCop.toFixed(1)}.
                      </div>
                    </div>
                  </div>
                </div>
                <div style={{ marginTop: 12, background: 'white', border: '1px solid #e5e7eb', borderRadius: 8, padding: 10 }}>
                  <div style={{ fontSize: '0.82rem', fontWeight: 600, marginBottom: 6 }}>Policy Intelligence Narrative</div>
                  <p style={{ margin: '0 0 6px 0', fontSize: '0.84rem', color: '#334155' }}>{decisionSummary.narratives.transportDominanceExplanation}</p>
                  <p style={{ margin: '0 0 6px 0', fontSize: '0.8rem', color: '#475569' }}>
                    Local policy sector ranking: {decisionSummary.localSectors
                      .slice()
                      .sort((a, b) => b.tons - a.tons)
                      .map((s) => `${s.label} ${s.percent.toFixed(1)}%`)
                      .join(' · ')}
                  </p>
                  <p style={{ margin: '0 0 6px 0', fontSize: '0.84rem', color: '#334155' }}>{decisionSummary.narratives.buildingsCostDynamicsExplanation}</p>
                  <p style={{ margin: '0 0 6px 0', fontSize: '0.84rem', color: '#334155' }}>{decisionSummary.narratives.electrificationOperatingCostNarrative}</p>
                  <p style={{ margin: '0 0 6px 0', fontSize: '0.84rem', color: '#334155' }}>{decisionSummary.narratives.capexVsOpexNarrative}</p>
                  <p style={{ margin: '0 0 6px 0', fontSize: '0.84rem', color: '#334155' }}>{decisionSummary.narratives.rateStructureNarrative}</p>
                  <p style={{ margin: '0 0 6px 0', fontSize: '0.84rem', color: '#334155' }}>{decisionSummary.narratives.recommendationNarrative[0]}</p>
                  <p style={{ margin: 0, fontSize: '0.84rem', color: '#334155' }}>{decisionSummary.narratives.recommendationNarrative[1]}</p>
                </div>
                <div style={{ marginTop: 12, background: 'white', border: '1px solid #e5e7eb', borderRadius: 8, padding: 10 }}>
                  <div style={{ fontSize: '0.82rem', fontWeight: 600, marginBottom: 6 }}>BAU Assumptions Summary</div>
                  <div style={{ fontSize: '0.8rem', color: '#475569' }}>
                    <div>Transport: {decisionSummary.bauAssumptions.transport}</div>
                    <div>Buildings: {decisionSummary.bauAssumptions.buildings}</div>
                    <div>Electricity decarbonization: {decisionSummary.bauAssumptions.electricity}</div>
                    <div>Waste: {decisionSummary.bauAssumptions.waste}</div>
                  </div>
                </div>
              </div>
            </details>
          )}

          {/* Portfolio Cost & Savings — collapsed by default */}
          {results && (
            <PortfolioCostSection
              buildingsCostResults={buildingsCostResults}
              transportCostResults={transportCostResults}
              population={getCachedBaseline(`baseline:${location}:emfac${emfacVersion}:buildingsBau${buildingsBauScenario}`)?.population ?? null}
              location={location}
              scenario={scenario}
              costView={portfolioCostView}
              onCostViewChange={setPortfolioCostView}
              emfacVersion={emfacVersion}
              buildingsBauScenario={buildingsBauScenario}
              defaultCollapsed
            />
          )}

          {/* Scenario Cost Comparison — same scenario across multiple locations */}
          {results && (
            <ScenarioCostAnalysisSection
              scenario={scenario}
              currentLocation={location}
              enabled={!!results}
              options={scenarioCostAnalysisOptions}
              costAssumptions={policyCostAssumptions}
              iceMpg={policyCostIceMpg}
              setIceMpg={setPolicyCostIceMpg}
              evKwhPerMile={policyCostEvKwhPerMile}
              setEvKwhPerMile={setPolicyCostEvKwhPerMile}
            />
          )}

          {/* Cost assumptions: last section — drives portfolio, decision summary, and scenario comparison */}
          {results && (
            <PolicyToolCostAssumptionsSection
              assumptions={policyCostAssumptions}
              setAssumptions={setPolicyCostAssumptions}
              iceMpg={policyCostIceMpg}
              setIceMpg={setPolicyCostIceMpg}
              evKwhPerMile={policyCostEvKwhPerMile}
              setEvKwhPerMile={setPolicyCostEvKwhPerMile}
              electricityPriceContext={electricityPriceContext}
              onResetCostAssumptions={resetPolicyCostAssumptions}
            />
          )}
        </section>
      </div>
    </div>
  );
};

export default PolicyImpactTool;
